import React, { useEffect, useState, useRef } from 'react';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Boxes,
  Building2,
  Camera,
  Check,
  CheckCircle,
  Clock,
  Database,
  Download,
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  Info,
  Key,
  Lock,
  Mail,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sofa,
  Trash2,
  Upload,
  WalletCards,
  X,
  XCircle,
  Zap
} from 'lucide-react';
import { ConfirmModal } from '../../components/ConfirmModal';
import FloatingField from '../../components/FloatingField';
import ModalShell from '../../components/ModalShell';
import UserAgreementDocument from '../../components/UserAgreementDocument';
import { API_URL } from '../../config';
import useToast from '../../hooks/useToast';
import { safeFetch } from '../../utils/safeFetch';
import { setUserTimezone } from '../../utils/timezone';
import { setDryCleaningEnabled } from '../../utils/featureFlags';
import { haptic } from '../../utils/vibration';
import '../../styles/Management.css';

const TIMEZONES = [
  { value: '2', label: 'UTC+2 Калининград' },
  { value: '3', label: 'UTC+3 Москва (по умолчанию)' },
  { value: '4', label: 'UTC+4 Самара' },
  { value: '5', label: 'UTC+5 Екатеринбург' },
  { value: '6', label: 'UTC+6 Омск' },
  { value: '7', label: 'UTC+7 Красноярск' },
  { value: '8', label: 'UTC+8 Иркутск' },
  { value: '9', label: 'UTC+9 Якутск' },
  { value: '10', label: 'UTC+10 Владивосток' },
  { value: '11', label: 'UTC+11 Магадан' },
  { value: '12', label: 'UTC+12 Камчатка' }
];

const SMTP_FIELDS = [
  { key: 'smtpHost', label: 'SMTP сервер', placeholder: 'smtp.yandex.ru' },
  { key: 'smtpPort', label: 'Порт', placeholder: '587', inputMode: 'numeric' },
  { key: 'smtpUsername', label: 'Логин', placeholder: 'billing@example.ru' },
  { key: 'smtpPassword', label: 'Пароль', placeholder: 'Пароль приложения', type: 'password' },
  { key: 'smtpFromEmail', label: 'Email отправителя', placeholder: 'billing@example.ru', type: 'email', inputMode: 'email' },
  { key: 'smtpFromName', label: 'Имя отправителя', placeholder: 'Большая Стирка' }
];

const S3_PROVIDERS = [
  {
    id: 'cloud_ru',
    name: 'Cloud.ru (Evolution Object Storage)',
    endpointUrl: 'https://s3.cloud.ru',
    region: 'ru-central-1',
    hint: 'Эндпоинт: https://s3.cloud.ru, регион: ru-central-1. Для подключения к Evolution Object Storage обязательно укажите ID тенанта (находится в консоли Cloud.ru: Хранение данных → Object Storage → Параметры работы с API).',
    linkText: 'Инструкция по ключам S3 и Tenant ID в Cloud.ru',
    linkUrl: 'https://cloud.ru/docs/s3e/ug/topics/api__api-key'
  },
  {
    id: 'cloud_ru_advanced',
    name: 'Cloud.ru (Advanced OBS)',
    endpointUrl: 'https://obs.ru-moscow-1.hc.sbercloud.ru',
    region: 'ru-moscow-1',
    hint: 'Корпоративное объектное хранилище Cloud.ru Advanced (Huawei). Эндпоинт: https://obs.ru-moscow-1.hc.sbercloud.ru, регион: ru-moscow-1.',
    linkText: 'Документация Advanced OBS',
    linkUrl: 'https://cloud.ru/docs/advanced-obs'
  },
  {
    id: 'yandex',
    name: 'Яндекс Облако (Yandex Object Storage)',
    endpointUrl: 'https://storage.yandexcloud.net',
    region: 'ru-central1',
    hint: 'Эндпоинт: https://storage.yandexcloud.net, регион: ru-central1. Статический ключ доступа создается в сервисном аккаунте каталога.',
    linkText: 'Документация Yandex Object Storage',
    linkUrl: 'https://cloud.yandex.ru/docs/storage/s3/'
  },
  {
    id: 'vk',
    name: 'VK Cloud (Mail.ru Hotbox)',
    endpointUrl: 'https://hb.bizmrg.com',
    region: 'ru-msk',
    hint: 'Эндпоинт: https://hb.bizmrg.com, регион: ru-msk. Ключи создаются в панели VK Cloud → Объектное хранилище → Аккаунты.',
    linkText: 'Документация VK Cloud',
    linkUrl: 'https://cloud.vk.com/docs/storage'
  },
  {
    id: 'selectel',
    name: 'Selectel (Объектное хранилище)',
    endpointUrl: 'https://s3.storage.selcloud.ru',
    region: 'ru-1',
    hint: 'Эндпоинт: https://s3.storage.selcloud.ru, регион: ru-1. Выпустите S3-ключ в панели управления Selectel.',
    linkText: 'Документация Selectel S3',
    linkUrl: 'https://selectel.ru/services/cloud/storage/'
  },
  {
    id: 'timeweb',
    name: 'Timeweb Cloud (S3)',
    endpointUrl: 'https://s3.timeweb.cloud',
    region: 'ru-1',
    hint: 'Эндпоинт: https://s3.timeweb.cloud, регион: ru-1. Ключи создаются в панели Timeweb Cloud в разделе S3.',
    linkText: 'Документация Timeweb S3',
    linkUrl: 'https://timeweb.cloud/docs/s3-storage'
  },
  {
    id: 'beget',
    name: 'Beget (S3)',
    endpointUrl: 'https://s3.beget.com',
    region: 'ru-1',
    hint: 'Эндпоинт: https://s3.beget.com, регион: ru-1. Ключи создаются в панели управления Beget в разделе Облачное хранилище.',
    linkText: 'Документация Beget S3',
    linkUrl: 'https://beget.com/ru/cloud/s3'
  },
  {
    id: 'aws',
    name: 'Amazon Web Services (AWS S3)',
    endpointUrl: 'https://s3.amazonaws.com',
    region: 'eu-central-1',
    hint: 'Amazon Web Services S3. Укажите региональный эндпоинт и регион (например eu-central-1).',
    linkText: 'Документация AWS S3',
    linkUrl: 'https://aws.amazon.com/s3/'
  },
  {
    id: 'custom',
    name: 'Другой провайдер / MinIO / Свой сервер',
    endpointUrl: '',
    region: 'ru-1',
    hint: 'Укажите произвольный адрес S3-совместимого сервера (например MinIO или Ceph) и регион.',
    linkText: '',
    linkUrl: ''
  }
];

const BOOLEAN_TRUE_VALUES = new Set(['1', 'true', 'yes', 'on', 'y', 'да']);

const parseBooleanSetting = (value, fallback = false) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return fallback;
  return BOOLEAN_TRUE_VALUES.has(normalized);
};

const StatusBadge = ({ status }) => {
  const configured = !!status?.configured;
  const missingCount = Array.isArray(status?.missing) ? status.missing.length : 0;
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ${
      configured
        ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-200'
        : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200'
    }`}>
      {configured ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {configured
        ? `Настроен${status?.source === 'env' ? ' в .env' : ''}`
        : missingCount > 0
          ? `Не настроен (${missingCount})`
          : 'Не настроен'}
    </span>
  );
};

const ALLOWED_ADMIN_TABS = ['company', 'settings', 'storage'];

const SystemManagement = ({ userType: propUserType }) => {
  const userType = propUserType ?? localStorage.getItem('user_type');
  const isDeveloper = userType === 'developer';
  const defaultTab = isDeveloper ? 'backups' : 'company';

  const { showToast } = useToast(3200);
  const fileInputRef = useRef(null);

  // Active tab: backups, company, integrations, settings, storage
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab') || localStorage.getItem('system_mgmt_tab');
      if (tabParam && (isDeveloper || ALLOWED_ADMIN_TABS.includes(tabParam))) {
        return tabParam;
      }
      return defaultTab;
    } catch {
      return defaultTab;
    }
  });

  useEffect(() => {
    if (!isDeveloper && !ALLOWED_ADMIN_TABS.includes(activeTab)) {
      setActiveTab('company');
    }
  }, [isDeveloper, activeTab]);

  const handleSelectTab = (tabId) => {
    haptic('selection');
    setActiveTab(tabId);
    try {
      localStorage.setItem('system_mgmt_tab', tabId);
    } catch {}
  };

  const [savingTab, setSavingTab] = useState(null);
  const [agreementMode, setAgreementMode] = useState('edit');
  const [savingAgreement, setSavingAgreement] = useState(false);
  const [togglingDryCleaning, setTogglingDryCleaning] = useState(false);
  const [storageCells, setStorageCells] = useState([]);
  const [storageCellsLoading, setStorageCellsLoading] = useState(false);
  const [storageCellSaving, setStorageCellSaving] = useState(false);
  const [newStorageCellName, setNewStorageCellName] = useState('');
  const [editingStorageCellId, setEditingStorageCellId] = useState(null);
  const [editingStorageCellName, setEditingStorageCellName] = useState('');
  const [isStorageModalOpen, setIsStorageModalOpen] = useState(false);
  const [draggedCellId, setDraggedCellId] = useState(null);
  const [touchDraggingId, setTouchDraggingId] = useState(null);
  const touchDraggedIdRef = useRef(null);
  const touchTargetIdRef = useRef(null);
  const storageCellsRef = useRef(storageCells);
  useEffect(() => {
    storageCellsRef.current = storageCells;
  }, [storageCells]);
  const [integrationStatus, setIntegrationStatus] = useState({
    yandex: { configured: false, source: null, missing: [] },
    dadata: { configured: false, source: null, missing: [] },
    invoiceCompany: { configured: false, source: null, missing: [] },
    smtp: { configured: false, source: null, missing: [] }
  });

  const [systemSettings, setSystemSettings] = useState({
    dryCleaningEnabled: true,
    dadataApiToken: '',
    yandexApiKey: '',
    timezone: '3',
    offlineDataTtlHours: '24',
    orderStartNumber: '6978',
    invoiceStartNumber: '1',
    expenseAutoApproveLimit: '0',
    companyName: '',
    companyFullName: '',
    companyLegalAddress: '',
    companyInn: '',
    companyKpp: '',
    companyOgrn: '',
    companyBankName: '',
    companyBankAccount: '',
    companyBankCorrAccount: '',
    companyBankBik: '',
    companyPhone: '',
    companyEmail: '',
    invoicePaymentTermsDays: '5',
    smtpHost: '',
    smtpPort: '587',
    smtpUsername: '',
    smtpPassword: '',
    smtpUseTls: true,
    smtpUseSsl: false,
    smtpFromEmail: '',
    smtpFromName: '',
    cleaningStorageCellsRequired: false,
    backupEnabled: false,
    backupTarget: 'local',
    backupTime: '03:00',
    backupRetentionDaysLocal: '7',
    backupRetentionDaysRemote: '30',
    backupNotifyTelegram: false,
    backupS3Provider: 'cloud_ru',
    backupS3TenantId: '',
    backupS3EndpointUrl: '',
    backupS3BucketName: '',
    backupS3Region: 'ru-central-1',
    backupS3AccessKey: '',
    backupS3SecretKey: '',
    backupTelegramBotToken: '',
    backupTelegramChatId: '',
    backupYandexDiskLogin: '',
    backupYandexDiskPassword: '',
    backupYandexDiskPath: '/backups',
    backupEncryptionEnabled: false,
    backupEncryptionPassword: '',
    mediaStorageTarget: 'local',
    mediaStorageCompressFull: true,
    mediaStorageDuplicateThumbnails: true,
    mediaStorageS3Provider: 'cloud_ru',
    mediaStorageS3TenantId: '',
    mediaStorageS3EndpointUrl: '',
    mediaStorageS3BucketName: '',
    mediaStorageS3Region: 'ru-central-1',
    mediaStorageS3AccessKey: '',
    mediaStorageS3SecretKey: '',
    mediaStorageTelegramBotToken: '',
    mediaStorageTelegramChatId: '',
    mediaStorageTelegramUseProxy: true,
    mediaStorageTelegramApiUrl: 'https://2.26.60.26:8443'
  });

  const [backupsList, setBackupsList] = useState([]);
  const [lastBackupStatus, setLastBackupStatus] = useState(null);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);
  const [testingDestination, setTestingDestination] = useState(false);
  const [testingMediaDestination, setTestingMediaDestination] = useState(false);
  const [testingSpeed, setTestingSpeed] = useState(false);
  const [speedTestResult, setSpeedTestResult] = useState(null);
  const [detectingMediaChat, setDetectingMediaChat] = useState(false);
  const [detectingBackupChat, setDetectingBackupChat] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState(null);
  const [deletingBackup, setDeletingBackup] = useState(false);

  // Restore Modal State
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [showRestorePassword, setShowRestorePassword] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showBackupPassword, setShowBackupPassword] = useState(false);

  const [agreementSettings, setAgreementSettings] = useState({
    version: '',
    title: '',
    subtitle: '',
    content: ''
  });

  const loadBackupsInfo = async () => {
    setLoadingBackups(true);
    try {
      const data = await safeFetch(`${API_URL}/api/backups`);
      if (data) {
        setBackupsList(Array.isArray(data.files) ? data.files : []);
        setLastBackupStatus(data.last_status || null);
      }
    } catch (error) {
      console.warn('Ошибка загрузки информации о бэкапах:', error);
    } finally {
      setLoadingBackups(false);
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const settingsObj = {};
          data.forEach(s => { settingsObj[s.key] = s.value; });
          setSystemSettings(prev => ({
            ...prev,
            dryCleaningEnabled: parseBooleanSetting(settingsObj.dry_cleaning_enabled, true),
            dadataApiToken: settingsObj.dadata_api_token || '',
            yandexApiKey: settingsObj.yandex_maps_api_key || settingsObj.yandex_geocoder_api_key || '',
            timezone: settingsObj.timezone || '3',
            offlineDataTtlHours: settingsObj.offline_data_ttl_hours || '24',
            orderStartNumber: settingsObj.order_start_number || prev.orderStartNumber,
            invoiceStartNumber: settingsObj.invoice_start_number || prev.invoiceStartNumber,
            expenseAutoApproveLimit: settingsObj.expense_auto_approve_limit || prev.expenseAutoApproveLimit,
            companyName: settingsObj.company_name || '',
            companyFullName: settingsObj.company_full_name || '',
            companyLegalAddress: settingsObj.company_legal_address || '',
            companyInn: settingsObj.company_inn || '',
            companyKpp: settingsObj.company_kpp || '',
            companyOgrn: settingsObj.company_ogrn || '',
            companyBankName: settingsObj.company_bank_name || '',
            companyBankAccount: settingsObj.company_bank_account || '',
            companyBankCorrAccount: settingsObj.company_bank_corr_account || '',
            companyBankBik: settingsObj.company_bank_bik || '',
            companyPhone: settingsObj.company_phone || '',
            companyEmail: settingsObj.company_email || '',
            invoicePaymentTermsDays: settingsObj.invoice_payment_terms_days || prev.invoicePaymentTermsDays,
            smtpHost: settingsObj.smtp_host || '',
            smtpPort: settingsObj.smtp_port || prev.smtpPort,
            smtpUsername: settingsObj.smtp_username || '',
            smtpPassword: settingsObj.smtp_password || '',
            smtpUseTls: parseBooleanSetting(settingsObj.smtp_use_tls, true),
            smtpUseSsl: parseBooleanSetting(settingsObj.smtp_use_ssl, false),
            smtpFromEmail: settingsObj.smtp_from_email || '',
            smtpFromName: settingsObj.smtp_from_name || '',
            cleaningStorageCellsRequired: parseBooleanSetting(settingsObj.cleaning_storage_cells_required, false),
            backupEnabled: parseBooleanSetting(settingsObj.backup_enabled, false),
            backupTarget: settingsObj.backup_target || 'local',
            backupTime: settingsObj.backup_time || '03:00',
            backupRetentionDaysLocal: settingsObj.backup_retention_days_local || '7',
            backupRetentionDaysRemote: settingsObj.backup_retention_days_remote || '30',
            backupNotifyTelegram: parseBooleanSetting(settingsObj.backup_notify_telegram, false),
            backupS3Provider: settingsObj.backup_s3_provider || (
              settingsObj.backup_s3_endpoint_url?.includes('cloud.ru') ? 'cloud_ru' :
              settingsObj.backup_s3_endpoint_url?.includes('sbercloud.ru') ? 'cloud_ru_advanced' :
              settingsObj.backup_s3_endpoint_url?.includes('yandexcloud.net') ? 'yandex' :
              settingsObj.backup_s3_endpoint_url?.includes('timeweb') ? 'timeweb' :
              settingsObj.backup_s3_endpoint_url?.includes('selcloud.ru') ? 'selectel' :
              settingsObj.backup_s3_endpoint_url?.includes('bizmrg.com') ? 'vk' :
              settingsObj.backup_s3_endpoint_url?.includes('beget.com') ? 'beget' :
              settingsObj.backup_s3_endpoint_url ? 'custom' : 'cloud_ru'
            ),
            backupS3TenantId: settingsObj.backup_s3_tenant_id || '',
            backupS3EndpointUrl: settingsObj.backup_s3_endpoint_url || '',
            backupS3BucketName: settingsObj.backup_s3_bucket_name || '',
            backupS3Region: settingsObj.backup_s3_region || 'ru-central-1',
            backupS3AccessKey: settingsObj.backup_s3_access_key || '',
            backupS3SecretKey: settingsObj.backup_s3_secret_key || '',
            backupTelegramBotToken: settingsObj.backup_telegram_bot_token || '',
            backupTelegramChatId: settingsObj.backup_telegram_chat_id || '',
            backupYandexDiskLogin: settingsObj.backup_yandex_disk_login || '',
            backupYandexDiskPassword: settingsObj.backup_yandex_disk_password || '',
            backupYandexDiskPath: settingsObj.backup_yandex_disk_path || '/backups',
            backupEncryptionEnabled: parseBooleanSetting(settingsObj.backup_encryption_enabled, false),
            backupEncryptionPassword: settingsObj.backup_encryption_password || '',
            mediaStorageTarget: settingsObj.media_storage_target || 'local',
            mediaStorageCompressFull: parseBooleanSetting(settingsObj.media_storage_compress_full, true),
            mediaStorageDuplicateThumbnails: parseBooleanSetting(settingsObj.media_storage_duplicate_thumbnails, true),
            mediaStorageS3Provider: settingsObj.media_storage_s3_provider || (
              settingsObj.media_storage_s3_endpoint_url?.includes('cloud.ru') ? 'cloud_ru' :
              settingsObj.media_storage_s3_endpoint_url?.includes('sbercloud.ru') ? 'cloud_ru_advanced' :
              settingsObj.media_storage_s3_endpoint_url?.includes('yandexcloud.net') ? 'yandex' :
              settingsObj.media_storage_s3_endpoint_url?.includes('timeweb') ? 'timeweb' :
              settingsObj.media_storage_s3_endpoint_url?.includes('selcloud.ru') ? 'selectel' :
              settingsObj.media_storage_s3_endpoint_url?.includes('bizmrg.com') ? 'vk' :
              settingsObj.media_storage_s3_endpoint_url?.includes('beget.com') ? 'beget' :
              settingsObj.media_storage_s3_endpoint_url ? 'custom' : 'cloud_ru'
            ),
            mediaStorageS3TenantId: settingsObj.media_storage_s3_tenant_id || '',
            mediaStorageS3EndpointUrl: settingsObj.media_storage_s3_endpoint_url || '',
            mediaStorageS3BucketName: settingsObj.media_storage_s3_bucket_name || '',
            mediaStorageS3Region: settingsObj.media_storage_s3_region || 'ru-central-1',
            mediaStorageS3AccessKey: settingsObj.media_storage_s3_access_key || '',
            mediaStorageS3SecretKey: settingsObj.media_storage_s3_secret_key || '',
            mediaStorageTelegramBotToken: settingsObj.media_storage_telegram_bot_token || '',
            mediaStorageTelegramChatId: settingsObj.media_storage_telegram_chat_id || '',
            mediaStorageTelegramUseProxy: settingsObj.media_storage_telegram_use_proxy !== undefined ? (settingsObj.media_storage_telegram_use_proxy === 'true') : true,
            mediaStorageTelegramApiUrl: settingsObj.media_storage_telegram_api_url || 'https://2.26.60.26:8443'
          }));
          if (settingsObj.dry_cleaning_enabled !== undefined) {
            setDryCleaningEnabled(parseBooleanSetting(settingsObj.dry_cleaning_enabled, true));
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error);
        showToast(error?.message || 'Не удалось загрузить настройки', 'error');
      }
    };
    loadSettings();
    if (isDeveloper) {
      loadBackupsInfo();
    }
    loadStorageCells();
    loadUserAgreement();
    loadIntegrationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserAgreement = async () => {
    try {
      const data = await safeFetch(`${API_URL}/api/user-agreement`, {}, 12000);
      setAgreementSettings({
        version: data.version || '',
        title: data.title || '',
        subtitle: data.subtitle || '',
        content: data.content || ''
      });
    } catch (error) {
      console.error('Ошибка загрузки пользовательского соглашения:', error);
      showToast(error?.message || 'Не удалось загрузить пользовательское соглашение', 'error');
    }
  };

  const loadIntegrationStatus = async () => {
    try {
      const data = await safeFetch(`${API_URL}/api/settings/integrations/status`);
      setIntegrationStatus({
        yandex: data.yandex || { configured: false, source: null, missing: [] },
        dadata: data.dadata || { configured: false, source: null, missing: [] },
        invoiceCompany: data.invoice_company || { configured: false, source: null, missing: [] },
        smtp: data.smtp || { configured: false, source: null, missing: [] }
      });
    } catch (error) {
      console.error('Ошибка загрузки статусов интеграций:', error);
      showToast(error?.message || 'Не удалось загрузить статусы интеграций', 'error');
    }
  };

  const loadStorageCells = async () => {
    setStorageCellsLoading(true);
    try {
      const data = await safeFetch(`${API_URL}/api/storage-cells?include_inactive=true`);
      setStorageCells(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast(error?.message || 'Не удалось загрузить ячейки', 'error');
    } finally {
      setStorageCellsLoading(false);
    }
  };

  const createStorageCell = async () => {
    const name = newStorageCellName.trim();
    if (!name || storageCellSaving) return;

    const existing = storageCells.find(
      (c) => c.name.trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      showToast(
        `Ячейка с названием «${existing.name}» уже существует${existing.is_active ? '' : ' (в архиве)'}`,
        'error'
      );
      return;
    }

    setStorageCellSaving(true);
    try {
      await safeFetch(`${API_URL}/api/storage-cells`, {
        method: 'POST',
        body: JSON.stringify({ name })
      });
      setNewStorageCellName('');
      showToast(`Ячейка «${name}» добавлена`, 'success');
      await loadStorageCells();
    } catch (error) {
      showToast(error?.message || 'Не удалось добавить ячейку', 'error');
    } finally {
      setStorageCellSaving(false);
    }
  };

  const saveStorageCellName = async (cellId) => {
    const name = editingStorageCellName.trim();
    if (!name || storageCellSaving) return;

    const existing = storageCells.find(
      (c) => c.id !== cellId && c.name.trim().toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      showToast(
        `Ячейка с названием «${existing.name}» уже существует${existing.is_active ? '' : ' (в архиве)'}`,
        'error'
      );
      return;
    }

    setStorageCellSaving(true);
    try {
      await safeFetch(`${API_URL}/api/storage-cells/${cellId}`, {
        method: 'PUT',
        body: JSON.stringify({ name })
      });
      setEditingStorageCellId(null);
      setEditingStorageCellName('');
      showToast('Название ячейки сохранено', 'success');
      await loadStorageCells();
    } catch (error) {
      showToast(error?.message || 'Не удалось переименовать ячейку', 'error');
    } finally {
      setStorageCellSaving(false);
    }
  };

  const setStorageCellActive = async (cell, isActive) => {
    if (storageCellSaving) return;
    if (!isActive && cell.active_placements > 0) {
      showToast(
        `Нельзя убрать в архив: в ячейке находятся предметы активных заказов (${cell.active_placements} шт.)`,
        'warning'
      );
      return;
    }
    setStorageCellSaving(true);
    try {
      await safeFetch(`${API_URL}/api/storage-cells/${cell.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: isActive })
      });
      showToast(isActive ? `Ячейка «${cell.name}» восстановлена` : `Ячейка «${cell.name}» убрана в архив`, 'success');
      await loadStorageCells();
    } catch (error) {
      showToast(error?.message || 'Не удалось изменить состояние ячейки', 'error');
    } finally {
      setStorageCellSaving(false);
    }
  };

  const reorderStorageCells = async (nextCellIds) => {
    if (storageCellSaving) return;
    const idMap = new Map(storageCells.map((c) => [c.id, c]));
    const reordered = nextCellIds.map((id) => idMap.get(id)).filter(Boolean);
    storageCells.forEach((c) => {
      if (!nextCellIds.includes(c.id)) reordered.push(c);
    });
    setStorageCells(reordered);

    setStorageCellSaving(true);
    try {
      await safeFetch(`${API_URL}/api/storage-cells/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ cell_ids: nextCellIds })
      });
      haptic('selection');
    } catch (error) {
      showToast(error?.message || 'Не удалось сохранить порядок ячеек', 'error');
      await loadStorageCells();
    } finally {
      setStorageCellSaving(false);
    }
  };

  const moveStorageCell = (cellId, direction) => {
    const visibleIds = storageCells.map((c) => c.id);
    const index = visibleIds.indexOf(cellId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= visibleIds.length) return;
    const nextIds = [...visibleIds];
    [nextIds[index], nextIds[targetIndex]] = [nextIds[targetIndex], nextIds[index]];
    reorderStorageCells(nextIds);
  };

  const handleCellDragStart = (event, cellId) => {
    setDraggedCellId(cellId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(cellId));
  };

  const handleCellDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleCellDrop = (event, targetCellId) => {
    event.preventDefault();
    if (!draggedCellId || draggedCellId === targetCellId) return;
    const currentIds = storageCells.map((c) => c.id);
    const fromIndex = currentIds.indexOf(draggedCellId);
    const toIndex = currentIds.indexOf(targetCellId);
    if (fromIndex === -1 || toIndex === -1) return;
    const nextIds = [...currentIds];
    const [removed] = nextIds.splice(fromIndex, 1);
    nextIds.splice(toIndex, 0, removed);
    setDraggedCellId(null);
    reorderStorageCells(nextIds);
  };

  const handleTouchStart = (event, cellId) => {
    touchDraggedIdRef.current = cellId;
    touchTargetIdRef.current = cellId;
    setTouchDraggingId(cellId);
    haptic('selection');
  };

  const handleTouchMove = (event) => {
    if (!touchDraggedIdRef.current) return;
    if (event.cancelable) event.preventDefault();
    const touch = event.touches[0];
    if (!touch) return;
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const targetCard = element?.closest('[data-cell-id]');
    const targetId = targetCard ? Number(targetCard.getAttribute('data-cell-id')) : null;
    if (targetId && targetId !== touchTargetIdRef.current) {
      touchTargetIdRef.current = targetId;
      haptic('selection');
      setStorageCells((previous) => {
        const ids = previous.map((c) => c.id);
        const fromIndex = ids.indexOf(touchDraggedIdRef.current);
        const toIndex = ids.indexOf(targetId);
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return previous;
        const next = [...previous];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
    }
  };

  const handleTouchEnd = () => {
    if (touchDraggedIdRef.current) {
      const finalIds = (storageCellsRef.current || []).map((c) => c.id);
      reorderStorageCells(finalIds);
      haptic('heavy');
    }
    touchDraggedIdRef.current = null;
    touchTargetIdRef.current = null;
    setTouchDraggingId(null);
  };

  const handleInputChange = (field, value) => {
    let nextValue = value;
    if (field === 'mediaStorageTelegramChatId' || field === 'backupTelegramChatId') {
      const trimmed = (value || '').trim();
      const matchK = trimmed.match(/web\.telegram\.org\/k\/#(-?\d+)/);
      const matchA = trimmed.match(/web\.telegram\.org\/a\/#(-?\d+)/);
      const matchC = trimmed.match(/t\.me\/c\/(\d+)/);
      if (matchK) {
        const raw = matchK[1];
        nextValue = raw.startsWith('-100') ? raw : (raw.startsWith('-') ? `-100${raw.slice(1)}` : `-100${raw}`);
      } else if (matchA) {
        const raw = matchA[1];
        nextValue = raw.startsWith('-100') ? raw : (raw.startsWith('-') ? `-100${raw.slice(1)}` : `-100${raw}`);
      } else if (matchC) {
        nextValue = `-100${matchC[1]}`;
      } else if (trimmed.startsWith('-') && !trimmed.startsWith('-100') && /^\d+$/.test(trimmed.slice(1)) && trimmed.length >= 9) {
        nextValue = `-100${trimmed.slice(1)}`;
      }
    } else if (field === 'mediaStorageTelegramBotToken' || field === 'backupTelegramBotToken') {
      const trimmed = (value || '').trim();
      if (trimmed.toLowerCase().startsWith('bot')) {
        nextValue = trimmed.slice(3).trim();
      }
    }
    setSystemSettings(prev => ({ ...prev, [field]: nextValue }));
  };

  const handleS3ProviderChange = (providerId) => {
    const selected = S3_PROVIDERS.find((p) => p.id === providerId);
    setSystemSettings((prev) => {
      const next = { ...prev, backupS3Provider: providerId };
      if (selected && providerId !== 'custom') {
        next.backupS3EndpointUrl = selected.endpointUrl;
        next.backupS3Region = selected.region;
      }
      return next;
    });
  };

  const handleMediaS3ProviderChange = (providerId) => {
    const selected = S3_PROVIDERS.find((p) => p.id === providerId);
    setSystemSettings((prev) => {
      const next = { ...prev, mediaStorageS3Provider: providerId };
      if (selected && providerId !== 'custom') {
        next.mediaStorageS3EndpointUrl = selected.endpointUrl;
        next.mediaStorageS3Region = selected.region;
      }
      return next;
    });
  };

  const copyS3FromBackupsToMedia = () => {
    haptic('tap');
    setSystemSettings((prev) => ({
      ...prev,
      mediaStorageS3Provider: prev.backupS3Provider,
      mediaStorageS3TenantId: prev.backupS3TenantId,
      mediaStorageS3EndpointUrl: prev.backupS3EndpointUrl,
      mediaStorageS3BucketName: prev.backupS3BucketName,
      mediaStorageS3Region: prev.backupS3Region,
      mediaStorageS3AccessKey: prev.backupS3AccessKey,
      mediaStorageS3SecretKey: prev.backupS3SecretKey,
    }));
    showToast('Параметры S3 скопированы из раздела «Резервные копии»', 'success');
  };

  const copyTelegramFromBackupsToMedia = () => {
    haptic('tap');
    setSystemSettings((prev) => ({
      ...prev,
      mediaStorageTelegramBotToken: prev.backupTelegramBotToken,
      mediaStorageTelegramChatId: prev.backupTelegramChatId,
    }));
    showToast('Параметры Telegram скопированы из раздела «Резервные копии»', 'success');
  };

  const copyS3FromMediaToBackups = () => {
    haptic('tap');
    setSystemSettings((prev) => ({
      ...prev,
      backupS3Provider: prev.mediaStorageS3Provider,
      backupS3TenantId: prev.mediaStorageS3TenantId,
      backupS3EndpointUrl: prev.mediaStorageS3EndpointUrl,
      backupS3BucketName: prev.mediaStorageS3BucketName,
      backupS3Region: prev.mediaStorageS3Region,
      backupS3AccessKey: prev.mediaStorageS3AccessKey,
      backupS3SecretKey: prev.mediaStorageS3SecretKey,
    }));
    showToast('Параметры S3 скопированы из раздела «Хранилище фото»', 'success');
  };

  const copyTelegramFromMediaToBackups = () => {
    haptic('tap');
    setSystemSettings((prev) => ({
      ...prev,
      backupTelegramBotToken: prev.mediaStorageTelegramBotToken,
      backupTelegramChatId: prev.mediaStorageTelegramChatId,
    }));
    showToast('Параметры Telegram скопированы из раздела «Хранилище фото»', 'success');
  };

  const handleTestMediaDestination = async () => {
    setTestingMediaDestination(true);
    try {
      const res = await safeFetch(`${API_URL}/api/settings/test-media-destination`, {
        method: 'POST',
        body: JSON.stringify({
          target: systemSettings.mediaStorageTarget,
          s3_tenant_id: systemSettings.mediaStorageS3TenantId,
          s3_endpoint_url: systemSettings.mediaStorageS3EndpointUrl,
          s3_bucket_name: systemSettings.mediaStorageS3BucketName,
          s3_access_key: systemSettings.mediaStorageS3AccessKey,
          s3_secret_key: systemSettings.mediaStorageS3SecretKey,
          s3_region: systemSettings.mediaStorageS3Region,
          telegram_bot_token: systemSettings.mediaStorageTelegramBotToken,
          telegram_chat_id: systemSettings.mediaStorageTelegramChatId,
          telegram_api_url: systemSettings.mediaStorageTelegramApiUrl,
          use_proxy: systemSettings.mediaStorageTelegramUseProxy,
        })
      });
      showToast(res.message || 'Проверка подключения прошла успешно!', 'success');
    } catch (err) {
      showToast(err?.message || 'Ошибка подключения к хранилищу медиа', 'error');
    } finally {
      setTestingMediaDestination(false);
    }
  };

  const handleTestTelegramSpeed = async () => {
    haptic('tap');
    setTestingSpeed(true);
    setSpeedTestResult(null);
    try {
      const res = await safeFetch(`${API_URL}/api/settings/test-telegram-speed`, {
        method: 'POST',
        body: JSON.stringify({
          bot_token: systemSettings.mediaStorageTelegramBotToken,
          telegram_api_url: systemSettings.mediaStorageTelegramApiUrl,
          use_proxy: systemSettings.mediaStorageTelegramUseProxy
        })
      });
      setSpeedTestResult(res);
      if (res.success) {
        showToast(res.message, 'success');
      } else {
        showToast(res.message || 'Ошибка замера скорости', 'error');
      }
    } catch (err) {
      setSpeedTestResult({
        success: false,
        latency_ms: 0,
        status: 'error',
        message: err?.message || 'Не удалось связаться с сервером',
        is_proxy: systemSettings.mediaStorageTelegramUseProxy
      });
      showToast(err?.message || 'Ошибка замера скорости', 'error');
    } finally {
      setTestingSpeed(false);
    }
  };

  const handleDetectTelegramChat = async (isMedia = true) => {
    const setLoader = isMedia ? setDetectingMediaChat : setDetectingBackupChat;
    const botToken = isMedia ? systemSettings.mediaStorageTelegramBotToken : systemSettings.backupTelegramBotToken;
    const targetField = isMedia ? 'mediaStorageTelegramChatId' : 'backupTelegramChatId';

    haptic('tap');
    setLoader(true);
    try {
      const res = await safeFetch(`${API_URL}/api/settings/detect-telegram-chat`, {
        method: 'POST',
        body: JSON.stringify({
          bot_token: botToken,
          telegram_api_url: isMedia ? systemSettings.mediaStorageTelegramApiUrl : '',
          use_proxy: isMedia ? systemSettings.mediaStorageTelegramUseProxy : true
        })
      });
      if (res.success && res.chat_id) {
        handleInputChange(targetField, res.chat_id);
        showToast(res.message, 'success');
      } else {
        showToast(res.message, 'info');
      }
    } catch (err) {
      showToast(err?.message || 'Не удалось определить чат Telegram', 'error');
    } finally {
      setLoader(false);
    }
  };

  const handleCheckboxChange = (field, checked) => {
    setSystemSettings((prev) => {
      if (field === 'smtpUseTls') {
        return {
          ...prev,
          smtpUseTls: checked,
          smtpUseSsl: checked ? false : prev.smtpUseSsl
        };
      }
      if (field === 'smtpUseSsl') {
        return {
          ...prev,
          smtpUseSsl: checked,
          smtpUseTls: checked ? false : prev.smtpUseTls
        };
      }
      return { ...prev, [field]: checked };
    });
  };

  const handleAgreementChange = (field, value) => {
    setAgreementSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAgreement = async () => {
    if (!agreementSettings.title.trim()) {
      showToast('Укажите заголовок соглашения', 'error');
      return;
    }
    if (agreementSettings.content.trim().length < 200) {
      showToast('Текст соглашения слишком короткий', 'error');
      return;
    }

    setSavingAgreement(true);
    try {
      const data = await safeFetch(`${API_URL}/api/user-agreement`, {
        method: 'PUT',
        body: JSON.stringify({
          title: agreementSettings.title,
          subtitle: agreementSettings.subtitle,
          content: agreementSettings.content
        })
      }, 12000);
      setAgreementSettings({
        version: data.version || '',
        title: data.title || '',
        subtitle: data.subtitle || '',
        content: data.content || ''
      });
      showToast(`Соглашение сохранено. Версия: ${data.version}`, 'success');
    } catch (error) {
      showToast(error?.message || 'Не удалось сохранить соглашение', 'error');
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleToggleDryCleaning = async () => {
    if (togglingDryCleaning) return;
    const nextVal = !systemSettings.dryCleaningEnabled;
    setTogglingDryCleaning(true);
    try {
      await safeFetch(`${API_URL}/api/settings/dry_cleaning_enabled`, {
        method: 'PUT',
        body: JSON.stringify({ value: nextVal ? 'true' : 'false' })
      });
      setSystemSettings(prev => ({ ...prev, dryCleaningEnabled: nextVal }));
      setDryCleaningEnabled(nextVal);
      showToast(
        nextVal ? 'Модуль «Химчистка мебели» включён' : 'Модуль «Химчистка мебели» отключён',
        'success'
      );
    } catch (err) {
      showToast(err?.message || 'Не удалось изменить статус модуля химчистки', 'error');
    } finally {
      setTogglingDryCleaning(false);
    }
  };

  const saveSettingsGroup = async (settingsToSave, tabId, successMsg, afterSave) => {
    setSavingTab(tabId);
    try {
      try {
        await safeFetch(`${API_URL}/api/settings/batch`, {
          method: 'PUT',
          body: JSON.stringify({ settings: settingsToSave })
        });
      } catch (batchErr) {
        // Fallback: поштучное сохранение настроек если batch недоступен
        await Promise.all(
          Object.entries(settingsToSave).map(([key, value]) =>
            safeFetch(`${API_URL}/api/settings/${key}`, {
              method: 'PUT',
              body: JSON.stringify({ value: value ?? '' })
            })
          )
        );
      }

      showToast(successMsg || 'Настройки сохранены', 'success');
      if (afterSave) {
        await afterSave();
      }
    } catch (err) {
      showToast(err?.message || 'Ошибка при сохранении настроек', 'error');
    } finally {
      setSavingTab(null);
    }
  };

  const handleSaveCompany = async () => {
    const payload = {
      company_name: systemSettings.companyName,
      company_full_name: systemSettings.companyFullName,
      company_legal_address: systemSettings.companyLegalAddress,
      company_inn: systemSettings.companyInn,
      company_kpp: systemSettings.companyKpp,
      company_ogrn: systemSettings.companyOgrn,
      company_phone: systemSettings.companyPhone,
      company_email: systemSettings.companyEmail,
      invoice_payment_terms_days: systemSettings.invoicePaymentTermsDays,
      company_bank_name: systemSettings.companyBankName,
      company_bank_bik: systemSettings.companyBankBik,
      company_bank_account: systemSettings.companyBankAccount,
      company_bank_corr_account: systemSettings.companyBankCorrAccount
    };
    await saveSettingsGroup(payload, 'company', 'Реквизиты организации сохранены', async () => {
      await loadIntegrationStatus();
    });
  };

  const handleSaveIntegrations = async () => {
    const payload = {
      dadata_api_token: systemSettings.dadataApiToken,
      yandex_maps_api_key: systemSettings.yandexApiKey,
      yandex_geocoder_api_key: systemSettings.yandexApiKey,
      smtp_host: systemSettings.smtpHost,
      smtp_port: systemSettings.smtpPort,
      smtp_username: systemSettings.smtpUsername,
      smtp_password: systemSettings.smtpPassword,
      smtp_use_tls: systemSettings.smtpUseTls ? 'true' : 'false',
      smtp_use_ssl: systemSettings.smtpUseSsl ? 'true' : 'false',
      smtp_from_email: systemSettings.smtpFromEmail,
      smtp_from_name: systemSettings.smtpFromName
    };
    await saveSettingsGroup(payload, 'integrations', 'Настройки интеграций сохранены', async () => {
      await loadIntegrationStatus();
    });
  };

  const handleSaveSystemSettings = async () => {
    const payload = {
      timezone: systemSettings.timezone,
      expense_auto_approve_limit: systemSettings.expenseAutoApproveLimit,
      dry_cleaning_enabled: systemSettings.dryCleaningEnabled ? 'true' : 'false',
      ...(isDeveloper ? {
        order_start_number: systemSettings.orderStartNumber,
        invoice_start_number: systemSettings.invoiceStartNumber,
        offline_data_ttl_hours: systemSettings.offlineDataTtlHours || '24'
      } : {})
    };
    await saveSettingsGroup(payload, 'settings', 'Параметры системы сохранены', async () => {
      setUserTimezone(systemSettings.timezone);
      setDryCleaningEnabled(systemSettings.dryCleaningEnabled);
    });
  };

  const handleSaveStorage = async () => {
    const payload = {
      cleaning_storage_cells_required: systemSettings.cleaningStorageCellsRequired ? 'true' : 'false'
    };
    await saveSettingsGroup(payload, 'storage', 'Настройки склада сохранены');
  };

  const handleSaveMedia = async () => {
    const payload = {
      media_storage_target: systemSettings.mediaStorageTarget,
      media_storage_compress_full: systemSettings.mediaStorageCompressFull ? 'true' : 'false',
      media_storage_duplicate_thumbnails: systemSettings.mediaStorageDuplicateThumbnails ? 'true' : 'false',
      media_storage_s3_provider: systemSettings.mediaStorageS3Provider,
      media_storage_s3_tenant_id: systemSettings.mediaStorageS3TenantId,
      media_storage_s3_endpoint_url: systemSettings.mediaStorageS3EndpointUrl,
      media_storage_s3_bucket_name: systemSettings.mediaStorageS3BucketName,
      media_storage_s3_region: systemSettings.mediaStorageS3Region,
      media_storage_s3_access_key: systemSettings.mediaStorageS3AccessKey,
      media_storage_s3_secret_key: systemSettings.mediaStorageS3SecretKey,
      media_storage_telegram_bot_token: systemSettings.mediaStorageTelegramBotToken,
      media_storage_telegram_chat_id: systemSettings.mediaStorageTelegramChatId,
      media_storage_telegram_use_proxy: systemSettings.mediaStorageTelegramUseProxy ? 'true' : 'false',
      media_storage_telegram_api_url: systemSettings.mediaStorageTelegramApiUrl
    };
    await saveSettingsGroup(payload, 'media', 'Настройки хранилища фото сохранены');
  };

  const handleSaveBackups = async () => {
    const payload = {
      backup_enabled: systemSettings.backupEnabled ? 'true' : 'false',
      backup_target: systemSettings.backupTarget,
      backup_time: systemSettings.backupTime,
      backup_retention_days_local: systemSettings.backupRetentionDaysLocal,
      backup_retention_days_remote: systemSettings.backupRetentionDaysRemote,
      backup_notify_telegram: systemSettings.backupNotifyTelegram ? 'true' : 'false',
      backup_s3_provider: systemSettings.backupS3Provider,
      backup_s3_tenant_id: systemSettings.backupS3TenantId,
      backup_s3_endpoint_url: systemSettings.backupS3EndpointUrl,
      backup_s3_bucket_name: systemSettings.backupS3BucketName,
      backup_s3_region: systemSettings.backupS3Region,
      backup_s3_access_key: systemSettings.backupS3AccessKey,
      backup_s3_secret_key: systemSettings.backupS3SecretKey,
      backup_telegram_bot_token: systemSettings.backupTelegramBotToken,
      backup_telegram_chat_id: systemSettings.backupTelegramChatId,
      backup_yandex_disk_login: systemSettings.backupYandexDiskLogin,
      backup_yandex_disk_password: systemSettings.backupYandexDiskPassword,
      backup_yandex_disk_path: systemSettings.backupYandexDiskPath,
      backup_encryption_enabled: systemSettings.backupEncryptionEnabled ? 'true' : 'false',
      backup_encryption_password: systemSettings.backupEncryptionPassword,
      backup_telegram_use_proxy: systemSettings.backupTelegramUseProxy ? 'true' : 'false',
      backup_telegram_api_url: systemSettings.backupTelegramApiUrl
    };
    await saveSettingsGroup(payload, 'backups', 'Параметры бэкапов сохранены', async () => {
      await loadBackupsInfo();
    });
  };

  const handleRunBackupNow = async () => {
    if (runningBackup) return;
    setRunningBackup(true);
    try {
      const res = await safeFetch(`${API_URL}/api/backups/run-now`, {
        method: 'POST'
      });
      showToast(`Бэкап успешно создан: ${res.filename} (${res.size_mb} МБ)`, 'success');
      await loadBackupsInfo();
    } catch (err) {
      showToast(err?.message || 'Ошибка создания бэкапа', 'error');
      await loadBackupsInfo();
    } finally {
      setRunningBackup(false);
    }
  };

  const handleTestDestination = async () => {
    setTestingDestination(true);
    try {
      const payload = {
        target: systemSettings.backupTarget,
        s3_tenant_id: systemSettings.backupS3TenantId,
        s3_endpoint_url: systemSettings.backupS3EndpointUrl,
        s3_bucket_name: systemSettings.backupS3BucketName,
        s3_region: systemSettings.backupS3Region,
        s3_access_key: systemSettings.backupS3AccessKey,
        s3_secret_key: systemSettings.backupS3SecretKey,
        telegram_bot_token: systemSettings.backupTelegramBotToken,
        telegram_chat_id: systemSettings.backupTelegramChatId,
        yandex_disk_login: systemSettings.backupYandexDiskLogin,
        yandex_disk_password: systemSettings.backupYandexDiskPassword,
        yandex_disk_path: systemSettings.backupYandexDiskPath
      };

      const res = await safeFetch(`${API_URL}/api/backups/test-destination`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      showToast(res.message || 'Подключение успешно!', 'success');
    } catch (err) {
      showToast(err?.message || 'Ошибка проверки подключения к хранилищу', 'error');
    } finally {
      setTestingDestination(false);
    }
  };

  const handleTestTelegramNotification = async () => {
    setTestingDestination(true);
    try {
      const res = await safeFetch(`${API_URL}/api/backups/test-destination`, {
        method: 'POST',
        body: JSON.stringify({
          target: 'telegram',
          telegram_bot_token: systemSettings.backupTelegramBotToken,
          telegram_chat_id: systemSettings.backupTelegramChatId
        })
      });
      showToast(res.message || 'Тестовое оповещение отправлено в Telegram!', 'success');
    } catch (err) {
      showToast(err?.message || 'Ошибка отправки в Telegram', 'error');
    } finally {
      setTestingDestination(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/backups/download/${encodeURIComponent(filename)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error(`Ошибка загрузки (${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err?.message || 'Не удалось скачать файл', 'error');
    }
  };

  const confirmDeleteBackup = async () => {
    if (!backupToDelete || deletingBackup) return;
    setDeletingBackup(true);
    try {
      await safeFetch(`${API_URL}/api/backups/${encodeURIComponent(backupToDelete)}`, {
        method: 'DELETE'
      });
      showToast(`Резервная копия «${backupToDelete}» удалена с сервера`, 'success');
      setBackupToDelete(null);
      await loadBackupsInfo();
    } catch (err) {
      showToast(err?.message || 'Ошибка удаления резервной копии', 'error');
    } finally {
      setDeletingBackup(false);
    }
  };

  // Restore handlers
  const openRestoreModalForFile = (file) => {
    haptic('selection');
    setRestoreTarget({
      isUpload: false,
      filename: file.filename,
      size_mb: file.size_mb,
      created_at: file.created_at,
      is_encrypted: !!file.is_encrypted
    });
    setRestorePassword(file.is_encrypted ? (systemSettings.backupEncryptionPassword || '') : '');
    setShowRestorePassword(false);
  };

  const handleUploadFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    haptic('selection');
    const isEncrypted = file.name.toLowerCase().endsWith('.zip');
    setRestoreTarget({
      isUpload: true,
      file: file,
      filename: file.name,
      size_mb: (file.size / (1024 * 1024)).toFixed(2),
      created_at: 'Загружаемый с компьютера',
      is_encrypted: isEncrypted
    });
    setRestorePassword(isEncrypted ? (systemSettings.backupEncryptionPassword || '') : '');
    setShowRestorePassword(false);
    e.target.value = '';
  };

  const executeRestore = async () => {
    if (!restoreTarget || restoring) return;
    setRestoring(true);
    try {
      if (restoreTarget.isUpload) {
        const formData = new FormData();
        formData.append('file', restoreTarget.file);
        if (restorePassword.trim()) {
          formData.append('password', restorePassword.trim());
        }
        const res = await safeFetch(`${API_URL}/api/backups/restore-upload`, {
          method: 'POST',
          body: formData
        });
        showToast(res.message || 'База данных успешно восстановлена!', 'success');
      } else {
        const res = await safeFetch(`${API_URL}/api/backups/restore`, {
          method: 'POST',
          body: JSON.stringify({
            filename: restoreTarget.filename,
            password: restorePassword.trim() || undefined
          })
        });
        showToast(res.message || 'База данных успешно восстановлена!', 'success');
      }
      setRestoreTarget(null);
      await loadBackupsInfo();
    } catch (err) {
      showToast(err?.message || 'Ошибка восстановления базы данных', 'error');
    } finally {
      setRestoring(false);
    }
  };

  const renderInputField = (field) => {
    const value = systemSettings[field.key] || '';
    return (
      <div key={field.key}>
        <FloatingField label={field.label}>
          <input
            type={field.type || 'text'}
            value={value}
            onChange={(event) => {
              const nextValue = event.target.value;
              const sanitizedValue = field.inputMode === 'numeric'
                ? nextValue.replace(/[^\d]/g, '')
                : nextValue;
              handleInputChange(field.key, sanitizedValue);
            }}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={field.placeholder || ''}
            inputMode={field.inputMode}
            autoComplete="off"
          />
        </FloatingField>
      </div>
    );
  };

  const allTabs = [
    {
      id: 'backups',
      label: 'Резервные копии',
      icon: Database,
      developerOnly: true,
      badge: systemSettings.backupEnabled ? 'Авто' : 'Выкл',
      badgeColor: systemSettings.backupEnabled ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    },
    {
      id: 'media',
      label: 'Хранилище фото',
      icon: Camera,
      developerOnly: true,
      badge: systemSettings.mediaStorageTarget === 's3' ? 'S3' : (systemSettings.mediaStorageTarget === 'telegram' ? 'Telegram' : 'Сервер'),
      badgeColor: systemSettings.mediaStorageTarget === 'local'
        ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
        : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
    },
    {
      id: 'company',
      label: 'Организация и оферта',
      icon: Building2,
      badge: integrationStatus.invoiceCompany.configured ? null : '!',
      badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
    },
    {
      id: 'integrations',
      label: 'Интеграции и API',
      icon: Mail,
      developerOnly: true,
      badge: null
    },
    {
      id: 'settings',
      label: 'Параметры системы',
      icon: Clock,
      badge: null
    },
    {
      id: 'storage',
      label: 'Ячейки склада',
      icon: Boxes,
      badge: storageCells.length > 0 ? String(storageCells.length) : null,
      badgeColor: 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
    }
  ];

  const TABS = allTabs.filter(tab => !tab.developerOnly || isDeveloper);

  return (
    <div className="space-y-4 animate-fade-in desktop-content-container-wide pb-16">
      {/* Скрытый инпут для загрузки резервной копии с компьютера */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".db.gz,.zip,.db"
        onChange={handleUploadFileChange}
        className="hidden"
      />

      {/* Верхняя карточка с переключением экранов */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <span>Системные настройки</span>
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {isDeveloper
              ? 'Управление резервными копиями, реквизитами, интеграциями и параметрами CRM'
              : 'Управление реквизитами, параметрами системы и ячейками склада'}
          </p>
        </div>

        {/* Навигационные табы экранов */}
        <div className="flex border-b border-gray-200 dark:border-gray-800 overflow-x-auto no-scrollbar gap-1 pt-1 -mx-4 px-4 sm:mx-0 sm:px-0">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleSelectTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-all shrink-0 ${
                  isActive
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50/60 dark:bg-orange-950/20 rounded-t-xl'
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800/40 rounded-t-xl'
                }`}
              >
                <Icon className={`h-4 w-4 ${isActive ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400 dark:text-gray-500'}`} />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${tab.badgeColor || 'bg-gray-100 text-gray-700'}`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* ЭКРАН 1: РЕЗЕРВНОЕ КОПИРОВАНИЕ И ВОССТАНОВЛЕНИЕ (ТОЛЬКО РАЗРАБОТЧИК)      */}
      {/* ========================================================================= */}
      {isDeveloper && activeTab === 'backups' && (
        <div className="space-y-4 animate-fade-in">
          {/* Панель быстрых действий */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold ${
                !systemSettings.backupEnabled
                  ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  : systemSettings.backupTarget === 's3'
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                    : systemSettings.backupTarget === 'telegram'
                      ? 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300'
                      : systemSettings.backupTarget === 'yandex_disk'
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                        : 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300'
              }`}>
                {systemSettings.backupEnabled ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                {!systemSettings.backupEnabled
                  ? 'Автоматический бэкап выключен'
                  : systemSettings.backupTarget === 's3'
                    ? 'Хранилище: Облако S3'
                    : systemSettings.backupTarget === 'telegram'
                      ? 'Хранилище: Telegram-чат'
                      : systemSettings.backupTarget === 'yandex_disk'
                        ? 'Хранилище: Яндекс Диск'
                        : 'Хранилище: Локальное'}
              </span>

              {systemSettings.backupEncryptionEnabled && (
                <span className="inline-flex items-center gap-1 rounded-xl bg-purple-50 px-2.5 py-1.5 text-xs font-semibold text-purple-700 dark:bg-purple-950/50 dark:text-purple-300">
                  <Lock className="h-3.5 w-3.5" />
                  Шифрование AES-256
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 shadow-sm"
              >
                <Upload className="h-3.5 w-3.5 text-gray-500" />
                Загрузить бэкап с ПК
              </button>

              <button
                type="button"
                onClick={handleRunBackupNow}
                disabled={runningBackup}
                className="inline-flex items-center gap-1.5 rounded-xl bg-orange-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-orange-700 disabled:opacity-50 shadow-sm"
              >
                {runningBackup ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Создание снимка...
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5" />
                    Создать копию сейчас
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Карточка последнего статуса */}
          {lastBackupStatus && (
            <div className={`rounded-2xl border p-4 shadow-sm ${
              lastBackupStatus.status === 'success'
                ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20'
                : 'border-red-200 bg-red-50/60 dark:border-red-900/40 dark:bg-red-950/20'
            }`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {lastBackupStatus.status === 'success' ? (
                    <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  )}
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    {lastBackupStatus.status === 'success' ? 'Последний бэкап выполнен успешно' : 'Ошибка последнего бэкапа'}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {lastBackupStatus.timestamp}
                </span>
              </div>
              {lastBackupStatus.status === 'success' ? (
                <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
                  <div>Файл: <code className="font-mono font-semibold">{lastBackupStatus.filename}</code> ({lastBackupStatus.size_mb} МБ)</div>
                  <div>
                    Хранилище: <span className="font-medium">{lastBackupStatus.target}</span> · Инициатор: {lastBackupStatus.actor || 'Система'}
                    {lastBackupStatus.details?.is_encrypted && (
                      <span className="ml-2 font-semibold text-purple-700 dark:text-purple-300">· 🔒 Зашифрован (AES-256)</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-xs text-red-700 dark:text-red-300">
                  Причина: {lastBackupStatus.error || 'Неизвестная ошибка'}
                </div>
              )}
            </div>
          )}

          {/* Список доступных резервных копий */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  Доступные копии на сервере
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Всего копий: {backupsList.length}
                </span>
              </div>
              <button
                type="button"
                onClick={loadBackupsInfo}
                disabled={loadingBackups}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
                title="Обновить список"
              >
                <RefreshCw className={`h-4 w-4 ${loadingBackups ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {backupsList.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-500 dark:text-gray-400">
                {loadingBackups ? 'Загрузка списка архивов...' : 'Пока нет созданных резервных копий.'}
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
                {backupsList.map((file) => (
                  <div
                    key={file.filename}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-800/40 dark:hover:bg-gray-800/80 transition-colors gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {file.filename}
                        </span>
                        {file.is_encrypted ? (
                          <span className="inline-flex items-center gap-1 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700 dark:bg-purple-950/60 dark:text-purple-300">
                            <Lock className="h-2.5 w-2.5" />
                            AES-256
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                            Gzip
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {file.created_at} · {file.size_mb} МБ
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5 self-end sm:self-center">
                      <button
                        type="button"
                        onClick={() => openRestoreModalForFile(file)}
                        className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/70 transition-colors"
                        title="Восстановить базу данных из этого бэкапа"
                      >
                        <RotateCcw className="h-3 w-3 text-amber-600" />
                        Восстановить
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDownloadBackup(file.filename)}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors"
                        title="Скачать архив на компьютер"
                      >
                        <Download className="h-3 w-3 text-gray-500" />
                        Скачать
                      </button>

                      <button
                        type="button"
                        onClick={() => setBackupToDelete(file.filename)}
                        className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white p-1.5 text-xs text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors"
                        title="Удалить архив с сервера"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Карточка: Безопасность и шифрование */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm space-y-3.5">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-800 pb-2.5">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  Шифрование резервных копий (AES-256)
                </h3>
              </div>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                Защита персональных данных
              </span>
            </div>

            <div className={`rounded-xl border p-3 transition-colors ${
              systemSettings.backupEncryptionEnabled
                ? 'border-purple-200 bg-purple-50/60 dark:border-purple-900/60 dark:bg-purple-950/20'
                : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/40'
            }`}>
              <label className="flex cursor-pointer items-start justify-between gap-4">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Шифровать архив бэкапа паролем
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-gray-600 dark:text-gray-300">
                    База данных упаковывается в формат ZIP с надёжным шифрованием <strong>AES-256</strong>. Архив можно открыть на любом компьютере в <strong>7-Zip</strong> или <strong>WinRAR</strong>, просто введя пароль.
                  </span>
                </span>
                <span className="relative mt-0.5 inline-flex shrink-0">
                  <input
                    type="checkbox"
                    checked={systemSettings.backupEncryptionEnabled}
                    onChange={(e) => {
                      haptic('selection');
                      handleCheckboxChange('backupEncryptionEnabled', e.target.checked);
                    }}
                    className="peer sr-only"
                  />
                  <span className="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-purple-600 peer-focus-visible:ring-2 peer-focus-visible:ring-purple-400 dark:bg-gray-700" />
                  <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                </span>
              </label>
            </div>

            {systemSettings.backupEncryptionEnabled && (
              <div className="pt-1">
                <FloatingField label="Пароль для шифрования архивов *">
                  <div className="relative">
                    <input
                      type={showBackupPassword ? 'text' : 'password'}
                      value={systemSettings.backupEncryptionPassword}
                      onChange={(e) => handleInputChange('backupEncryptionPassword', e.target.value)}
                      placeholder="Задайте надежный пароль шифрования"
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pr-10 text-sm font-mono dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowBackupPassword(!showBackupPassword)}
                      className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      {showBackupPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FloatingField>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  💡 Обязательно сохраните этот пароль. Без него восстановить базу данных из зашифрованного архива будет невозможно.
                </p>
              </div>
            )}
          </div>

          {/* Карточка: Параметры хранилища */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2.5">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                Настройки автоматического бэкапа и хранилища
              </h3>
            </div>

            {/* Автобэкап переключатель */}
            <div className={`rounded-xl border p-3 transition-colors ${
              systemSettings.backupEnabled
                ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/20'
                : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/40'
            }`}>
              <label className="flex cursor-pointer items-start justify-between gap-4">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Автоматическое резервное копирование по расписанию
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-gray-600 dark:text-gray-300">
                    Каждые сутки выполняет горячий снимок базы SQLite через Online Backup API, проверяет целостность и упаковывает файл.
                  </span>
                </span>
                <span className="relative mt-0.5 inline-flex shrink-0">
                  <input
                    type="checkbox"
                    checked={systemSettings.backupEnabled}
                    onChange={(e) => {
                      haptic('selection');
                      handleCheckboxChange('backupEnabled', e.target.checked);
                    }}
                    className="peer sr-only"
                  />
                  <span className="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-emerald-500 peer-focus-visible:ring-2 peer-focus-visible:ring-emerald-400 dark:bg-gray-700" />
                  <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                </span>
              </label>
            </div>

            {/* Выбор целевого хранилища */}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Место сохранения резервной копии
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {[
                  { id: 'local', title: 'Только локально', desc: 'Хранение архивов на сервере' },
                  { id: 's3', title: 'Облако S3 (+ копия на сервере)', desc: 'Cloud.ru, Яндекс, Selectel, Timeweb, Beget' },
                  { id: 'telegram', title: 'Telegram-чат (+ копия на сервере)', desc: 'Отправка архива документом в чат' },
                  { id: 'yandex_disk', title: 'Яндекс Диск (+ копия на сервере)', desc: 'WebDAV загрузка на диск' }
                ].map((opt) => {
                  const active = systemSettings.backupTarget === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        handleInputChange('backupTarget', opt.id);
                      }}
                      className={`flex flex-col rounded-xl border p-3 text-left transition-all ${
                        active
                          ? 'border-orange-500 bg-orange-50/70 text-orange-950 ring-1 ring-orange-500 dark:bg-orange-950/20 dark:text-orange-100'
                          : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600'
                      }`}
                    >
                      <span className="text-sm font-semibold">{opt.title}</span>
                      <span className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Параметры S3 хранилища */}
            {systemSettings.backupTarget === 's3' && (() => {
              const currentProvider = S3_PROVIDERS.find((p) => p.id === systemSettings.backupS3Provider) || S3_PROVIDERS[0];
              return (
                <div className="space-y-3.5 rounded-xl border border-orange-200 bg-orange-50/30 p-3.5 dark:border-orange-900/40 dark:bg-orange-950/10">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-orange-800 dark:text-orange-300">
                      Параметры S3 хранилища
                    </div>
                    <span className="text-[11px] text-orange-700/80 dark:text-orange-400/80">
                      S3-совместимое объектное хранилище
                    </span>
                  </div>

                  {(systemSettings.mediaStorageS3BucketName || systemSettings.mediaStorageS3EndpointUrl) && (
                    <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl border border-blue-200 bg-blue-50/70 text-xs text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/20 dark:text-blue-200">
                      <div>
                        💡 <strong>Параметры S3 уже настроены в разделе «Хранилище фото»</strong> ({systemSettings.mediaStorageS3BucketName || systemSettings.mediaStorageS3EndpointUrl})
                      </div>
                      <button
                        type="button"
                        onClick={copyS3FromMediaToBackups}
                        className="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      >
                        Использовать эти параметры
                      </button>
                    </div>
                  )}

                  <FloatingField label="Провайдер хранилища (Облако)">
                    <select
                      value={systemSettings.backupS3Provider}
                      onChange={(e) => handleS3ProviderChange(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    >
                      {S3_PROVIDERS.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                  </FloatingField>

                  {currentProvider && (
                    <div className="rounded-xl border border-blue-200/70 bg-blue-50/60 p-3 text-xs leading-relaxed text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200">
                      <div className="font-medium">{currentProvider.hint}</div>
                      {currentProvider.id === 'cloud_ru' && (
                        <div className="mt-1 text-[11px] text-blue-800 dark:text-blue-300">
                          💡 <strong>Для Cloud.ru (Evolution):</strong> эндпоинт <code className="rounded bg-blue-100 px-1 py-0.5 font-mono text-[11px] text-blue-900 dark:bg-blue-900/60 dark:text-blue-200">https://s3.cloud.ru</code>, регион <code className="rounded bg-blue-100 px-1 py-0.5 font-mono text-[11px] text-blue-900 dark:bg-blue-900/60 dark:text-blue-200">ru-central-1</code>. Сервисному аккаунту обязательно выдайте роль <code className="rounded bg-blue-100 px-1 py-0.5 font-mono text-[11px] text-blue-900 dark:bg-blue-900/60 dark:text-blue-200">s3e.editor</code> (или Администратор бакета).
                        </div>
                      )}
                      {currentProvider.linkUrl && (
                        <div className="mt-1.5">
                          <a
                            href={currentProvider.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-semibold text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {currentProvider.linkText} ↗
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid gap-3 md:grid-cols-2">
                    <FloatingField label="Имя бакета (Bucket Name) *">
                      <input
                        type="text"
                        value={systemSettings.backupS3BucketName}
                        onChange={(e) => handleInputChange('backupS3BucketName', e.target.value)}
                        placeholder="например: bucket-a212d0"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    {systemSettings.backupS3Provider === 'cloud_ru' && (
                      <FloatingField label="ID тенанта (Tenant ID) *">
                        <input
                          type="text"
                          value={systemSettings.backupS3TenantId}
                          onChange={(e) => handleInputChange('backupS3TenantId', e.target.value)}
                          placeholder="например: d2484de0-7566-4b17-9d7b-b52a29b1fb01"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </FloatingField>
                    )}

                    <FloatingField label={systemSettings.backupS3Provider === 'cloud_ru' ? "Key ID (логин ключа) *" : "Access Key ID (Key ID) *"}>
                      <input
                        type="text"
                        value={systemSettings.backupS3AccessKey}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (systemSettings.backupS3Provider === 'cloud_ru' && val.includes(':')) {
                            const [pTenant, pKey] = val.split(':');
                            if (pTenant.includes('-') && pKey) {
                              setSystemSettings(prev => ({
                                ...prev,
                                backupS3TenantId: prev.backupS3TenantId || pTenant,
                                backupS3AccessKey: pKey
                              }));
                              return;
                            }
                          }
                          handleInputChange('backupS3AccessKey', val);
                        }}
                        placeholder="Key ID из консоли облака"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    <FloatingField label="S3 Endpoint URL *">
                      <input
                        type="text"
                        value={systemSettings.backupS3EndpointUrl}
                        onChange={(e) => handleInputChange('backupS3EndpointUrl', e.target.value)}
                        placeholder="https://s3.cloud.ru"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    <FloatingField label="Регион S3">
                      <input
                        type="text"
                        value={systemSettings.backupS3Region}
                        onChange={(e) => handleInputChange('backupS3Region', e.target.value)}
                        placeholder="ru-central-1"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    <div className="md:col-span-2">
                      <FloatingField label="Secret Access Key *">
                        <input
                          type="password"
                          value={systemSettings.backupS3SecretKey}
                          onChange={(e) => handleInputChange('backupS3SecretKey', e.target.value)}
                          placeholder="Секретный ключ S3"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </FloatingField>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Поддерживаются любые S3: Cloud.ru, Яндекс, Selectel, Timeweb, VK Cloud, Beget, AWS, MinIO.
                    </span>
                    <button
                      type="button"
                      onClick={handleTestDestination}
                      disabled={testingDestination}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    >
                      {testingDestination ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                      Проверить подключение к S3
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Telegram настройки */}
            {systemSettings.backupTarget === 'telegram' && (
              <div className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/30 p-3.5 dark:border-sky-900/40 dark:bg-sky-950/10">
                <div className="text-xs font-semibold uppercase tracking-wider text-sky-800 dark:text-sky-300">
                  Параметры Telegram-бота
                </div>

                {(systemSettings.mediaStorageTelegramBotToken || systemSettings.mediaStorageTelegramChatId) && (
                  <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl border border-sky-200 bg-sky-50/70 text-xs text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-sky-200">
                    <div>
                      💡 <strong>Telegram-бот уже настроен в разделе «Хранилище фото»</strong>
                    </div>
                    <button
                      type="button"
                      onClick={copyTelegramFromMediaToBackups}
                      className="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-lg bg-sky-600 text-white hover:bg-sky-700 transition-colors"
                    >
                      Использовать эти параметры
                    </button>
                  </div>
                )}

                <div className="grid gap-3 md:grid-cols-2">
                  <FloatingField label="Telegram Bot Token">
                    <input
                      type="password"
                      value={systemSettings.backupTelegramBotToken}
                      onChange={(e) => handleInputChange('backupTelegramBotToken', e.target.value)}
                      placeholder="123456789:ABCdefGh..."
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                  </FloatingField>
                  <div className="space-y-1">
                    <FloatingField label="Telegram Chat ID">
                      <input
                        type="text"
                        value={systemSettings.backupTelegramChatId}
                        onChange={(e) => handleInputChange('backupTelegramChatId', e.target.value)}
                        placeholder="-1001234567890"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>
                    <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                      <span>💡 Для канала: <code className="font-mono text-sky-600 dark:text-sky-400 font-semibold">-1003955309484</code></span>
                      <button
                        type="button"
                        onClick={() => handleDetectTelegramChat(false)}
                        disabled={detectingBackupChat || !systemSettings.backupTelegramBotToken}
                        className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 dark:text-sky-400 font-medium disabled:opacity-50"
                      >
                        {detectingBackupChat ? (
                          <div className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                        ) : (
                          <Search className="h-3 w-3" />
                        )}
                        Определить ID
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Бот отправит архив документом в указанный чат.
                  </span>
                  <button
                    type="button"
                    onClick={handleTestDestination}
                    disabled={testingDestination}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                  >
                    {testingDestination ? (
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                    Отправить тест в Telegram
                  </button>
                </div>
              </div>
            )}

            {/* Яндекс Диск настройки */}
            {systemSettings.backupTarget === 'yandex_disk' && (
              <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/30 p-3.5 dark:border-amber-900/40 dark:bg-amber-950/10">
                <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                  Параметры Яндекс Диска (WebDAV)
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <FloatingField label="Логин Яндекс">
                    <input
                      type="text"
                      value={systemSettings.backupYandexDiskLogin}
                      onChange={(e) => handleInputChange('backupYandexDiskLogin', e.target.value)}
                      placeholder="login@yandex.ru"
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                  </FloatingField>
                  <FloatingField label="Пароль приложения Яндекс Диска">
                    <input
                      type="password"
                      value={systemSettings.backupYandexDiskPassword}
                      onChange={(e) => handleInputChange('backupYandexDiskPassword', e.target.value)}
                      placeholder="Пароль приложения"
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                  </FloatingField>
                </div>
                <FloatingField label="Папка на Диске">
                  <input
                    type="text"
                    value={systemSettings.backupYandexDiskPath}
                    onChange={(e) => handleInputChange('backupYandexDiskPath', e.target.value)}
                    placeholder="/backups"
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </FloatingField>
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Пароль создаётся в Яндекс ID → Безопасность → Пароли приложений → Файлы.
                  </span>
                  <button
                    type="button"
                    onClick={handleTestDestination}
                    disabled={testingDestination}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                  >
                    {testingDestination ? (
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    Проверить подключение
                  </button>
                </div>
              </div>
            )}

            {/* Telegram алерты */}
            {systemSettings.backupTarget !== 'telegram' && (
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
                <label className="flex items-center gap-3 text-sm text-gray-900 dark:text-gray-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={systemSettings.backupNotifyTelegram}
                    onChange={(e) => handleCheckboxChange('backupNotifyTelegram', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="font-semibold">Отправлять отчет о создании бэкапа и алерты в Telegram</span>
                </label>
                {systemSettings.backupNotifyTelegram && (
                  <div className="mt-3 space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid gap-3 md:grid-cols-2">
                      <FloatingField label="Telegram Bot Token">
                        <input
                          type="password"
                          value={systemSettings.backupTelegramBotToken}
                          onChange={(e) => handleInputChange('backupTelegramBotToken', e.target.value)}
                          placeholder="123456789:ABCdefGh..."
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </FloatingField>
                      <FloatingField label="Telegram Chat ID">
                        <input
                          type="text"
                          value={systemSettings.backupTelegramChatId}
                          onChange={(e) => handleInputChange('backupTelegramChatId', e.target.value)}
                          placeholder="-1001234567890"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </FloatingField>
                    </div>
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        onClick={handleTestTelegramNotification}
                        disabled={testingDestination}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                      >
                        {testingDestination ? (
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                        ) : (
                          <Send className="h-3 w-3" />
                        )}
                        Отправить тестовое оповещение
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Расписание и сроки хранения */}
            <div className="grid gap-3 md:grid-cols-3 pt-2">
              <FloatingField label="Время ежедневного запуска (UTC+3)">
                <input
                  type="time"
                  value={systemSettings.backupTime}
                  onChange={(e) => handleInputChange('backupTime', e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </FloatingField>
              <FloatingField label="Срок хранения локально, дней">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={systemSettings.backupRetentionDaysLocal}
                  onChange={(e) => handleInputChange('backupRetentionDaysLocal', e.target.value.replace(/\D/g, ''))}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </FloatingField>
              {systemSettings.backupTarget === 's3' && (
                <FloatingField label="Срок хранения в S3, дней">
                  <input
                    type="number"
                    min="1"
                    max="730"
                    value={systemSettings.backupRetentionDaysRemote}
                    onChange={(e) => handleInputChange('backupRetentionDaysRemote', e.target.value.replace(/\D/g, ''))}
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </FloatingField>
              )}
            </div>

            {/* Кнопка сохранения параметров бэкапов */}
            <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={handleSaveBackups}
                disabled={savingTab === 'backups'}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-50"
              >
                {savingTab === 'backups' ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Сохранение...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Сохранить параметры бэкапов
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ЭКРАН: ХРАНИЛИЩЕ МЕДИАФАЙЛОВ И ФОТО (ТОЛЬКО РАЗРАБОТЧИК)                   */}
      {/* ========================================================================= */}
      {isDeveloper && activeTab === 'media' && (
        <div className="space-y-4 animate-fade-in">
          {/* Информационная панель статуса */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold ${
                systemSettings.mediaStorageTarget === 's3'
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                  : systemSettings.mediaStorageTarget === 'telegram'
                    ? 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300'
                    : 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300'
              }`}>
                <CheckCircle className="h-3.5 w-3.5" />
                {systemSettings.mediaStorageTarget === 's3'
                  ? 'Хранилище фото: Облако S3'
                  : systemSettings.mediaStorageTarget === 'telegram'
                    ? 'Хранилище фото: Telegram-канал'
                    : 'Хранилище фото: Сервер (uploads/)'}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {systemSettings.mediaStorageCompressFull ? 'Сжатие: включено (~250 КБ)' : 'Сжатие: выключено (исходный файл)'}
              </span>
            </div>

            <button
              type="button"
              onClick={handleSaveMedia}
              disabled={savingTab === 'media'}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {savingTab === 'media' ? 'Сохранение...' : 'Сохранить настройки'}
            </button>
          </div>

          {/* Карточка настроек хранилища фото */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-4">
            <div className="border-b border-gray-100 dark:border-gray-800 pb-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Camera className="h-4 w-4 text-orange-600" />
                Целевое хранилище для фото заказов и задач
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Определяет, куда отправляются полноразмерные фотографии, прикрепляемые курьерами и цехом
              </span>
            </div>

            {/* Выбор места сохранения */}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Куда сохранять фотографии
              </div>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                {[
                  { id: 'local', title: 'Локальный диск', desc: 'На сервере в uploads/. Максимальная скорость, без внешних сервисов.' },
                  { id: 's3', title: 'Облако S3', desc: 'Cloud.ru (Free Tier), Яндекс, Selectel, Timeweb, VK Cloud, Beget.' },
                  { id: 'telegram', title: 'Telegram-канал', desc: 'Бесплатное бесконечное хранилище в закрытом канале через Telegram Bot API.' }
                ].map((opt) => {
                  const active = systemSettings.mediaStorageTarget === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        handleInputChange('mediaStorageTarget', opt.id);
                      }}
                      className={`flex flex-col rounded-xl border p-3.5 text-left transition-all ${
                        active
                          ? 'border-orange-500 bg-orange-50/70 text-orange-950 ring-1 ring-orange-500 dark:bg-orange-950/20 dark:text-orange-100'
                          : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600'
                      }`}
                    >
                      <span className="text-sm font-semibold">{opt.title}</span>
                      <span className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Параметры S3 хранилища для медиа */}
            {systemSettings.mediaStorageTarget === 's3' && (() => {
              const currentProvider = S3_PROVIDERS.find((p) => p.id === systemSettings.mediaStorageS3Provider) || S3_PROVIDERS[0];
              return (
                <div className="space-y-3.5 rounded-xl border border-orange-200 bg-orange-50/30 p-3.5 dark:border-orange-900/40 dark:bg-orange-950/10">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-orange-800 dark:text-orange-300">
                      Параметры S3 для фотографий
                    </div>
                    <span className="text-[11px] text-orange-700/80 dark:text-orange-400/80">
                      Объектное хранилище
                    </span>
                  </div>

                  {/* Кнопка синхронизации с бэкапами */}
                  {(systemSettings.backupS3BucketName || systemSettings.backupS3EndpointUrl) && (
                    <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl border border-blue-200 bg-blue-50/70 text-xs text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/20 dark:text-blue-200">
                      <div>
                        💡 <strong>Параметры S3 уже настроены в разделе «Резервные копии»</strong> ({systemSettings.backupS3BucketName || systemSettings.backupS3EndpointUrl})
                      </div>
                      <button
                        type="button"
                        onClick={copyS3FromBackupsToMedia}
                        className="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      >
                        Использовать параметры из бэкапов
                      </button>
                    </div>
                  )}

                  <FloatingField label="Провайдер хранилища (Облако)">
                    <select
                      value={systemSettings.mediaStorageS3Provider}
                      onChange={(e) => handleMediaS3ProviderChange(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    >
                      {S3_PROVIDERS.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                  </FloatingField>

                  {currentProvider && (
                    <div className="rounded-xl border border-blue-200/70 bg-blue-50/60 p-3 text-xs leading-relaxed text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200">
                      <div className="font-medium">{currentProvider.hint}</div>
                      {currentProvider.id === 'cloud_ru' && (
                        <div className="mt-1 text-[11px] text-blue-800 dark:text-blue-300">
                          💡 <strong>Для Cloud.ru (Evolution):</strong> эндпоинт <code className="rounded bg-blue-100 px-1 py-0.5 font-mono text-[11px] text-blue-900 dark:bg-blue-900/60 dark:text-blue-200">https://s3.cloud.ru</code>, регион <code className="rounded bg-blue-100 px-1 py-0.5 font-mono text-[11px] text-blue-900 dark:bg-blue-900/60 dark:text-blue-200">ru-central-1</code>. Можно использовать тот же бакет, что и для бэкапов.
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid gap-3 md:grid-cols-2">
                    <FloatingField label="Имя бакета (Bucket Name) *">
                      <input
                        type="text"
                        value={systemSettings.mediaStorageS3BucketName}
                        onChange={(e) => handleInputChange('mediaStorageS3BucketName', e.target.value)}
                        placeholder="например: bucket-a212d0"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    {systemSettings.mediaStorageS3Provider === 'cloud_ru' && (
                      <FloatingField label="ID тенанта (Tenant ID) *">
                        <input
                          type="text"
                          value={systemSettings.mediaStorageS3TenantId}
                          onChange={(e) => handleInputChange('mediaStorageS3TenantId', e.target.value)}
                          placeholder="например: d2484de0-7566-4b17-9d7b-b52a29b1fb01"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </FloatingField>
                    )}

                    <FloatingField label={systemSettings.mediaStorageS3Provider === 'cloud_ru' ? "Key ID (логин ключа) *" : "Access Key ID *"}>
                      <input
                        type="text"
                        value={systemSettings.mediaStorageS3AccessKey}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (systemSettings.mediaStorageS3Provider === 'cloud_ru' && val.includes(':')) {
                            const [pTenant, pKey] = val.split(':');
                            if (pTenant.includes('-') && pKey) {
                              setSystemSettings(prev => ({
                                ...prev,
                                mediaStorageS3TenantId: prev.mediaStorageS3TenantId || pTenant,
                                mediaStorageS3AccessKey: pKey
                              }));
                              return;
                            }
                          }
                          handleInputChange('mediaStorageS3AccessKey', val);
                        }}
                        placeholder="Key ID из консоли облака"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    <FloatingField label="S3 Endpoint URL *">
                      <input
                        type="text"
                        value={systemSettings.mediaStorageS3EndpointUrl}
                        onChange={(e) => handleInputChange('mediaStorageS3EndpointUrl', e.target.value)}
                        placeholder="https://s3.cloud.ru"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    <FloatingField label="Регион S3">
                      <input
                        type="text"
                        value={systemSettings.mediaStorageS3Region}
                        onChange={(e) => handleInputChange('mediaStorageS3Region', e.target.value)}
                        placeholder="ru-central-1"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>

                    <div className="md:col-span-2">
                      <FloatingField label="Secret Access Key *">
                        <input
                          type="password"
                          value={systemSettings.mediaStorageS3SecretKey}
                          onChange={(e) => handleInputChange('mediaStorageS3SecretKey', e.target.value)}
                          placeholder="Секретный ключ S3"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </FloatingField>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Фото сохраняются в бакет с префиксом <code>photos/orders/</code> и <code>photos/tasks/</code>
                    </span>
                    <button
                      type="button"
                      onClick={handleTestMediaDestination}
                      disabled={testingMediaDestination}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    >
                      {testingMediaDestination ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                      Проверить подключение к S3
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Telegram настройки для медиа */}
            {systemSettings.mediaStorageTarget === 'telegram' && (
              <div className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/30 p-3.5 dark:border-sky-900/40 dark:bg-sky-950/10">
                <div className="text-xs font-semibold uppercase tracking-wider text-sky-800 dark:text-sky-300">
                  Параметры Telegram-хранилища
                </div>

                {/* Кнопка синхронизации с бэкапами */}
                {(systemSettings.backupTelegramBotToken || systemSettings.backupTelegramChatId) && (
                  <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl border border-sky-200 bg-sky-50/70 text-xs text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-sky-200">
                    <div>
                      💡 <strong>Telegram-бот уже настроен в разделе «Резервные копии»</strong>
                    </div>
                    <button
                      type="button"
                      onClick={copyTelegramFromBackupsToMedia}
                      className="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-lg bg-sky-600 text-white hover:bg-sky-700 transition-colors"
                    >
                      Использовать параметры из бэкапов
                    </button>
                  </div>
                )}

                <div className="rounded-xl border border-sky-200/80 bg-sky-50/80 p-3 text-xs leading-relaxed text-sky-900 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-200">
                  <div className="font-semibold mb-1">ℹ️ Как работает хранилище в Telegram:</div>
                  <ol className="list-decimal pl-4 space-y-1">
                    <li>Создайте приватный канал или группу в Telegram (например, <em>«БС — Архив фото заказов»</em>).</li>
                    <li>Добавьте вашего бота в канал и назначьте его <strong>администратором</strong> с правом отправки сообщений/файлов. <em>(Без прав администратора Telegram не разрешит боту доступ в закрытый канал)</em>.</li>
                    <li>Укажите токен бота и ID канала (например: <code className="bg-sky-100 px-1 py-0.5 rounded font-mono">-1001234567890</code>). Можно вставить ссылку на канал из Telegram Web — ID определится автоматически.</li>
                    <li>При просмотре фото на сайте или в приложении сервер сам скачивает файл из Telegram и отдаёт пользователю. Пользователю Telegram не требуется!</li>
                  </ol>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <FloatingField label="Telegram Bot Token *">
                    <input
                      type="password"
                      value={systemSettings.mediaStorageTelegramBotToken}
                      onChange={(e) => handleInputChange('mediaStorageTelegramBotToken', e.target.value)}
                      placeholder="123456789:ABCdefGh..."
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                  </FloatingField>
                  <div className="space-y-1">
                    <FloatingField label="ID канала или чата (Chat ID) *">
                      <input
                        type="text"
                        value={systemSettings.mediaStorageTelegramChatId}
                        onChange={(e) => handleInputChange('mediaStorageTelegramChatId', e.target.value)}
                        placeholder="-1001234567890"
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </FloatingField>
                    <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                      <span>💡 Для канала: <code className="font-mono text-sky-600 dark:text-sky-400 font-semibold">-1003955309484</code></span>
                      <button
                        type="button"
                        onClick={() => handleDetectTelegramChat(true)}
                        disabled={detectingMediaChat || !systemSettings.mediaStorageTelegramBotToken}
                        className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700 dark:text-sky-400 font-medium disabled:opacity-50"
                      >
                        {detectingMediaChat ? (
                          <div className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                        ) : (
                          <Search className="h-3 w-3" />
                        )}
                        Определить ID автоматически
                      </button>
                    </div>
                  </div>
                </div>

                {/* Переключатель Nginx Reverse Proxy */}
                <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3.5 dark:border-sky-900/40 dark:bg-sky-950/20">
                  <label className="flex cursor-pointer items-start justify-between gap-4">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                        <ShieldCheck className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                        Использовать Nginx Reverse Proxy (обход замедлений ТСПУ/РКН)
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                        Маршрутизирует трафик через защищенный европейский сервер для мгновенной загрузки и отдачи фото без таймаутов.
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      checked={systemSettings.mediaStorageTelegramUseProxy}
                      onChange={(e) => {
                        handleInputChange('mediaStorageTelegramUseProxy', e.target.checked);
                        if (e.target.checked && !systemSettings.mediaStorageTelegramApiUrl) {
                          handleInputChange('mediaStorageTelegramApiUrl', 'https://2.26.60.26:8443');
                        }
                      }}
                      className="mt-1 h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                    />
                  </label>

                  {systemSettings.mediaStorageTelegramUseProxy ? (
                    <div className="mt-3 pt-3 border-t border-sky-200/60 dark:border-sky-900/40 space-y-1">
                      <FloatingField label="Адрес Nginx Reverse Proxy">
                        <input
                          type="text"
                          value={systemSettings.mediaStorageTelegramApiUrl}
                          onChange={(e) => handleInputChange('mediaStorageTelegramApiUrl', e.target.value)}
                          placeholder="https://2.26.60.26:8443"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 font-mono text-xs"
                        />
                      </FloatingField>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">
                        ⚡ Настроен ваш рабочий сервер: <code className="font-mono text-sky-600 dark:text-sky-400">https://2.26.60.26:8443</code> (порт 8443 HTTPS).
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                      ⚠️ Прокси выключен. Запросы идут напрямую на <code className="font-mono">api.telegram.org</code> (возможны потери пакетов и задержки ТСПУ).
                    </div>
                  )}
                </div>

                {/* Результаты замера скорости */}
                {speedTestResult && (
                  <div className={`p-3 rounded-xl border text-xs flex items-center justify-between gap-3 ${
                    speedTestResult.status === 'excellent'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                      : speedTestResult.status === 'slow'
                      ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
                      : 'border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300'
                  }`}>
                    <div className="flex items-center gap-2.5">
                      <span className="text-base shrink-0">
                        {speedTestResult.status === 'excellent' ? '⚡' : speedTestResult.status === 'slow' ? '⏱️' : '🛑'}
                      </span>
                      <div>
                        <div className="font-bold flex items-center gap-2">
                          <span>Задержка: {speedTestResult.latency_ms} мс</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            speedTestResult.is_proxy
                              ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {speedTestResult.is_proxy ? 'Через Nginx Proxy' : 'Напрямую'}
                          </span>
                        </div>
                        <div className="text-[11px] opacity-85 mt-0.5">
                          {speedTestResult.message}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSpeedTestResult(null)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm font-bold shrink-0 p-1"
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Файлы хранятся в Telegram бессрочно и бесплатно.
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleTestTelegramSpeed}
                      disabled={testingSpeed || !systemSettings.mediaStorageTelegramBotToken}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                    >
                      {testingSpeed ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-amber-600 border-t-transparent" />
                      ) : (
                        <Zap className="h-3.5 w-3.5" />
                      )}
                      Тест скорости
                    </button>
                    <button
                      type="button"
                      onClick={handleTestMediaDestination}
                      disabled={testingMediaDestination}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    >
                      {testingMediaDestination ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                      ) : (
                        <Send className="h-3 w-3" />
                      )}
                      Проверить отправку
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Карточка оптимизации и сжатия */}
            <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Оптимизация и миниатюры
              </div>

              {/* Переключатель сжатия */}
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
                <label className="flex cursor-pointer items-start justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Сжимать полноразмерные фото перед сохранением
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-gray-600 dark:text-gray-300">
                      {systemSettings.mediaStorageCompressFull
                        ? 'Включено: фото сжимаются до разрешения ~1600-2000px (JPEG качество 82%). Объём уменьшается на 80-90% (до ~250-400 КБ) без визуальной потери качества, экономя место и трафик.'
                        : 'Выключено: в хранилище отправляется исходный «сырой» файл с камеры любого объёма (5-15 МБ).'}
                    </span>
                  </span>
                  <span className="relative mt-0.5 inline-flex shrink-0">
                    <input
                      type="checkbox"
                      checked={systemSettings.mediaStorageCompressFull}
                      onChange={(e) => {
                        haptic('selection');
                        handleInputChange('mediaStorageCompressFull', e.target.checked);
                      }}
                      className="h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </span>
                </label>
              </div>

              {/* Дублирование миниатюр */}
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 dark:border-gray-700 dark:bg-gray-800/40">
                <label className="flex cursor-pointer items-start justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Дублировать миниатюры в облако / Telegram
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-gray-600 dark:text-gray-300">
                      Миниатюры (~30 КБ) всегда хранятся локально на сервере для моментального открытия карточек. Включение этой опции создаёт резервную копию миниатюры в S3/Telegram на случай аварии сервера.
                    </span>
                  </span>
                  <span className="relative mt-0.5 inline-flex shrink-0">
                    <input
                      type="checkbox"
                      checked={systemSettings.mediaStorageDuplicateThumbnails}
                      onChange={(e) => {
                        haptic('selection');
                        handleInputChange('mediaStorageDuplicateThumbnails', e.target.checked);
                      }}
                      className="h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </span>
                </label>
              </div>
            </div>

            {/* Кнопка сохранения внизу */}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleSaveMedia}
                disabled={savingTab === 'media'}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {savingTab === 'media' ? 'Сохранение...' : 'Сохранить настройки хранилища фото'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ЭКРАН 2: ОРГАНИЗАЦИЯ И ОФЕРТА                                            */}
      {/* ========================================================================= */}
      {activeTab === 'company' && (
        <div className="space-y-4 animate-fade-in">
          {/* Реквизиты организации */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-800 pb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-orange-600" />
                  Реквизиты организации для счетов
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Эти данные выводятся в PDF-счетах на оплату и актах для юридических лиц
                </span>
              </div>
              <StatusBadge status={integrationStatus.invoiceCompany} />
            </div>

            {/* Блок 1: Основные сведения */}
            <div className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Основные сведения
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <FloatingField label="Краткое название">
                  <input
                    type="text"
                    value={systemSettings.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                    placeholder="Большая Стирка"
                    autoComplete="off"
                  />
                </FloatingField>
                <FloatingField label="Полное наименование">
                  <input
                    type="text"
                    value={systemSettings.companyFullName}
                    onChange={(e) => handleInputChange('companyFullName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                    placeholder="ООО «Большая Стирка»"
                    autoComplete="off"
                  />
                </FloatingField>
              </div>
              <div>
                <FloatingField label="Юридический адрес">
                  <input
                    type="text"
                    value={systemSettings.companyLegalAddress}
                    onChange={(e) => handleInputChange('companyLegalAddress', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                    placeholder="123456, г. Москва, ул. Примерная, д. 1"
                    autoComplete="off"
                  />
                </FloatingField>
              </div>
            </div>

            {/* Блок 2: Налоговые реквизиты и оплата */}
            <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Регистрационные данные и условия
              </div>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <FloatingField label="ИНН">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={12}
                    value={systemSettings.companyInn}
                    onChange={(e) => handleInputChange('companyInn', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-mono"
                    placeholder="7701234567"
                    autoComplete="off"
                  />
                </FloatingField>
                <FloatingField label="КПП">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={9}
                    value={systemSettings.companyKpp}
                    onChange={(e) => handleInputChange('companyKpp', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-mono"
                    placeholder="770101001"
                    autoComplete="off"
                  />
                </FloatingField>
                <FloatingField label="ОГРН / ОГРНИП">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={15}
                    value={systemSettings.companyOgrn}
                    onChange={(e) => handleInputChange('companyOgrn', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-mono"
                    placeholder="1027700000000"
                    autoComplete="off"
                  />
                </FloatingField>
                <FloatingField label="Срок оплаты, дней">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={3}
                    value={systemSettings.invoicePaymentTermsDays}
                    onChange={(e) => handleInputChange('invoicePaymentTermsDays', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-mono"
                    placeholder="5"
                    autoComplete="off"
                  />
                </FloatingField>
              </div>
            </div>

            {/* Блок 3: Контакты */}
            <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Контакты для счетов
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <FloatingField label="Телефон">
                  <input
                    type="tel"
                    inputMode="tel"
                    value={systemSettings.companyPhone}
                    onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                    placeholder="+7 900 000-00-00"
                    autoComplete="off"
                  />
                </FloatingField>
                <FloatingField label="Email компании">
                  <input
                    type="email"
                    inputMode="email"
                    value={systemSettings.companyEmail}
                    onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                    placeholder="info@example.ru"
                    autoComplete="off"
                  />
                </FloatingField>
              </div>
            </div>

            {/* Блок 4: Банковские реквизиты */}
            <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Банковские реквизиты
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <FloatingField label="Банк">
                    <input
                      type="text"
                      value={systemSettings.companyBankName}
                      onChange={(e) => handleInputChange('companyBankName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                      placeholder="АО «Банк»"
                      autoComplete="off"
                    />
                  </FloatingField>
                </div>
                <div>
                  <FloatingField label="БИК">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={9}
                      value={systemSettings.companyBankBik}
                      onChange={(e) => handleInputChange('companyBankBik', e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-mono"
                      placeholder="044525225"
                      autoComplete="off"
                    />
                  </FloatingField>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <FloatingField label="Расчётный счёт">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={20}
                    value={systemSettings.companyBankAccount}
                    onChange={(e) => handleInputChange('companyBankAccount', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-mono"
                    placeholder="40702810900000000001"
                    autoComplete="off"
                  />
                </FloatingField>
                <FloatingField label="Корреспондентский счёт">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={20}
                    value={systemSettings.companyBankCorrAccount}
                    onChange={(e) => handleInputChange('companyBankCorrAccount', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-mono"
                    placeholder="30101810400000000225"
                    autoComplete="off"
                  />
                </FloatingField>
              </div>
            </div>

            {/* Кнопка сохранения реквизитов */}
            <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={handleSaveCompany}
                disabled={savingTab === 'company'}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-50"
              >
                {savingTab === 'company' ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Сохранение...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Сохранить реквизиты
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Пользовательское соглашение / Оферта */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-800 pb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Пользовательское соглашение (Оферта)
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Текст договора-оферты, отображаемый клиентам при входе и оформлении
                </span>
              </div>
              <span className="rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                Версия: {agreementSettings.version || 'не задана'}
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <FloatingField label="Заголовок соглашения">
                <input
                  type="text"
                  value={agreementSettings.title}
                  onChange={(event) => handleAgreementChange('title', event.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                  placeholder="Пользовательское соглашение"
                />
              </FloatingField>
              <FloatingField label="Подзаголовок">
                <input
                  type="text"
                  value={agreementSettings.subtitle}
                  onChange={(event) => handleAgreementChange('subtitle', event.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm"
                  placeholder="Для пользователей информационной системы"
                />
              </FloatingField>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Текст договора-оферты
                </label>
                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-100/70 dark:bg-gray-800 text-xs">
                  <button
                    type="button"
                    onClick={() => setAgreementMode('edit')}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md font-medium transition-colors ${
                      agreementMode === 'edit'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                    }`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Редактор
                  </button>
                  <button
                    type="button"
                    onClick={() => setAgreementMode('preview')}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md font-medium transition-colors ${
                      agreementMode === 'preview'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                    }`}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Предпросмотр
                  </button>
                </div>
              </div>

              {agreementMode === 'edit' ? (
                <div>
                  <textarea
                    rows={14}
                    value={agreementSettings.content}
                    onChange={(event) => handleAgreementChange('content', event.target.value)}
                    className="w-full h-80 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 font-mono text-sm leading-6 resize-none"
                    placeholder="Введите текст соглашения. Заголовки можно начинать с ##, пункты списка - с дефиса."
                  />
                  <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      Форматирование: строки с <strong className="font-semibold text-gray-700 dark:text-gray-200">##</strong> — заголовки, с дефисом <strong className="font-semibold text-gray-700 dark:text-gray-200">-</strong> — пункты списка.
                    </span>
                    <span className="font-mono">
                      Символов: {agreementSettings.content?.length || 0}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="h-80 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50/70 dark:bg-gray-800/40">
                  <UserAgreementDocument
                    agreement={{
                      title: agreementSettings.title || 'Пользовательское соглашение',
                      subtitle: agreementSettings.subtitle,
                      content: agreementSettings.content,
                      version: agreementSettings.version || 'Черновик'
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end pt-1 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={handleSaveAgreement}
                disabled={savingAgreement}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-semibold shadow-sm"
              >
                {savingAgreement ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Сохранение...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Сохранить соглашение и выпустить новую версию
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ЭКРАН 3: ИНТЕГРАЦИИ И API (ТОЛЬКО РАЗРАБОТЧИК)                            */}
      {/* ========================================================================= */}
      {isDeveloper && activeTab === 'integrations' && (
        <div className="space-y-4 animate-fade-in">
          {/* DaData API */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2.5">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Key className="h-4 w-4 text-purple-600" />
                  API токен DaData
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Автоматические подсказки адресов, стандартизация и реквизиты юрлиц
                </span>
              </div>
              <StatusBadge status={integrationStatus.dadata} />
            </div>

            <FloatingField label="API токен DaData">
              <input
                type="password"
                value={systemSettings.dadataApiToken}
                onChange={(e) => handleInputChange('dadataApiToken', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 font-mono"
                placeholder="Токен из личного кабинета dadata.ru"
              />
            </FloatingField>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Используется сервером для безопасных подсказок. Токен хранится на сервере и не утекает в браузер.
            </p>
          </div>

          {/* Яндекс Карты API */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2.5">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Key className="h-4 w-4 text-red-600" />
                  API ключ Яндекс Карты и Геокодер
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Карты в карточках заказов, построение маршрутов и геокодирование адресов
                </span>
              </div>
              <StatusBadge status={integrationStatus.yandex} />
            </div>

            <FloatingField label="API ключ Яндекс">
              <input
                type="password"
                value={systemSettings.yandexApiKey}
                onChange={(e) => handleInputChange('yandexApiKey', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 font-mono"
                placeholder="Ключ JavaScript API и HTTP Геокодера"
              />
            </FloatingField>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Выпускается в Кабинете разработчика Яндекс (developer.tech.yandex.ru).
            </p>
          </div>

          {/* Почтовый сервер SMTP */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2.5">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  Почтовый сервер (SMTP) для счетов
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Используется для отправки PDF-счетов клиентам по кнопке «Отправить счёт»
                </span>
              </div>
              <StatusBadge status={integrationStatus.smtp} />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {SMTP_FIELDS.map(renderInputField)}
            </div>

            <div className="grid gap-3 md:grid-cols-2 pt-1">
              <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={systemSettings.smtpUseTls}
                  onChange={(event) => handleCheckboxChange('smtpUseTls', event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Использовать STARTTLS
              </label>

              <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100 cursor-pointer">
                <input
                  type="checkbox"
                  checked={systemSettings.smtpUseSsl}
                  onChange={(event) => handleCheckboxChange('smtpUseSsl', event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Использовать SSL
              </label>
            </div>

            {/* Кнопка сохранения настроек интеграций */}
            <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={handleSaveIntegrations}
                disabled={savingTab === 'integrations'}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-50"
              >
                {savingTab === 'integrations' ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Сохранение...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Сохранить настройки интеграций
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ЭКРАН 4: ПАРАМЕТРЫ СИСТЕМЫ                                               */}
      {/* ========================================================================= */}
      {activeTab === 'settings' && (
        <div className="space-y-4 animate-fade-in">
          {/* Модуль химчистки мебели */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Sofa className="h-4 w-4 text-orange-600" />
                Модуль «Химчистка мебели»
              </h3>
              <div className="flex items-center gap-3">
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    systemSettings.dryCleaningEnabled
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  {systemSettings.dryCleaningEnabled ? 'Включена' : 'Отключена'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={systemSettings.dryCleaningEnabled}
                  aria-label="Переключить модуль химчистки мебели"
                  disabled={togglingDryCleaning}
                  onClick={handleToggleDryCleaning}
                  className="toggle-switch shrink-0 disabled:opacity-50"
                  style={{
                    '--toggle-bg': systemSettings.dryCleaningEnabled ? 'rgb(59 130 246)' : 'rgb(156 163 175)',
                    '--toggle-height': '26px',
                    '--toggle-width': '44px',
                    '--knob-x': systemSettings.dryCleaningEnabled ? '20px' : '2px'
                  }}
                >
                  <span className="toggle-switch-knob" />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              {systemSettings.dryCleaningEnabled ? (
                <span>
                  Приём заказов на выездную химчистку мебели <strong>активен</strong>. В форме создания заказа доступен выбор типа «Химчистка мебели», а у сотрудников отображается рабочий этап.
                </span>
              ) : (
                <span>
                  Химчистка мебели <strong>отключена</strong>. Новые заказы создаются сразу как обычная «Стирка» (поле выбора скрыто), этап химчистки скрыт у сотрудников. Все прошлые заказы химчистки сохранены в журнале и статистике.
                </span>
              )}
            </p>
            {!systemSettings.dryCleaningEnabled && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 rounded-xl p-3 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2.5">
                <Info className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
                <span className="leading-relaxed">
                  Модуль можно в любой момент включить обратно — все настройки, каталог и прайсы химчистки сохраняются в системе без изменений.
                </span>
              </div>
            )}
          </div>

          {/* Часовой пояс */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-3">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-2.5">
              <Clock className="h-4 w-4 text-orange-600" />
              Часовой пояс системы
            </h3>
            <FloatingField label="Часовой пояс">
              <select
                value={systemSettings.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm font-medium"
              >
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </FloatingField>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Выбранный часовой пояс используется для отображения времени в заказах, логах активности, новостях и расписании бэкапов.
            </p>
          </div>

          {/* Финансовые лимиты */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-3">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-2.5">
              <WalletCards className="h-4 w-4 text-emerald-600" />
              Финансовые лимиты
            </h3>
            <FloatingField label="Лимит расхода без подтверждения, ₽">
              <input
                type="number"
                min="0"
                step="1"
                value={systemSettings.expenseAutoApproveLimit}
                onChange={(e) => handleInputChange('expenseAutoApproveLimit', e.target.value.replace(/[^\d.]/g, ''))}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100"
                placeholder="0"
              />
            </FloatingField>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Если сумма расхода сотрудника превышает этот лимит, операция уйдёт на подтверждение администратору. Значение <strong>0</strong> — подтверждение не требуется.
            </p>
          </div>

          {/* Нумерация заказов и счетов - ТОЛЬКО ДЛЯ РАЗРАБОТЧИКА */}
          {isDeveloper && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-2.5">
                <Building2 className="h-4 w-4 text-orange-600" />
                Нумерация заказов и счетов
              </h3>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <FloatingField label="Стартовый номер заказа">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={systemSettings.orderStartNumber}
                      onChange={(e) => handleInputChange('orderStartNumber', e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100"
                      placeholder="Например: 6978"
                    />
                  </FloatingField>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Следующий заказ получит номер не ниже этого значения.
                  </p>
                </div>

                <div>
                  <FloatingField label="Стартовый номер счёта">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={systemSettings.invoiceStartNumber}
                      onChange={(e) => handleInputChange('invoiceStartNumber', e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100"
                      placeholder="Например: 1"
                    />
                  </FloatingField>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Номер счёта присваивается при первой отправке или формировании PDF.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Безопасность офлайн-режима - ТОЛЬКО ДЛЯ РАЗРАБОТЧИКА */}
          {isDeveloper && (
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-2.5">
                <ShieldCheck className="h-4 w-4 text-blue-600" />
                Безопасность офлайн-режима
              </h3>
              <div>
                <FloatingField label="Срок жизни офлайн-данных на устройствах (в часах)">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={systemSettings.offlineDataTtlHours}
                    onChange={(e) => handleInputChange('offlineDataTtlHours', e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:text-gray-100"
                    placeholder="24"
                  />
                </FloatingField>

                {/* Быстрые кнопки выбора */}
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {[
                    { label: '12 ч.', value: '12' },
                    { label: '24 ч. (сутки)', value: '24' },
                    { label: '48 ч. (2 дня)', value: '48' },
                    { label: '72 ч. (3 дня)', value: '72' },
                    { label: 'Без лимита', value: '0' }
                  ].map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => handleInputChange('offlineDataTtlHours', preset.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${
                        systemSettings.offlineDataTtlHours === preset.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-600 font-semibold'
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <p className="mt-2.5 text-xs text-gray-500 dark:text-gray-400">
                  Максимальное время, в течение которого сотрудник может работать с локально сохранёнными заказами без связи с сервером.
                  Если устройство не выходит в сеть дольше указанного времени, сохранённые данные заказов и клиентов будут автоматически удалены при следующей попытке открыть приложение.
                  Значение <strong>0</strong> отключает автоудаление по времени.
                </p>
              </div>
            </div>
          )}

          {/* Кнопка сохранения параметров системы */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={handleSaveSystemSettings}
              disabled={savingTab === 'settings'}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:opacity-50"
            >
              {savingTab === 'settings' ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Сохранение...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Сохранить параметры системы
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ЭКРАН 5: ЯЧЕЙКИ СКЛАДА                                                   */}
      {/* ========================================================================= */}
      {activeTab === 'storage' && (
        <div className="space-y-4 animate-fade-in">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-orange-600" />
                  Ячейки хранения для потока «Чистка»
                </h3>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Используются цехом при размещении предметов на сушке и складе.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  haptic('open');
                  setIsStorageModalOpen(true);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 active:scale-95 shrink-0"
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>Настроить ячейки и порядок</span>
              </button>
            </div>

            <div className={`rounded-xl border p-3.5 transition-colors ${
              systemSettings.cleaningStorageCellsRequired
                ? 'border-orange-200 bg-orange-50/60 dark:border-orange-900/60 dark:bg-orange-950/20'
                : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/40'
            }`}>
              <label className="flex cursor-pointer items-start justify-between gap-4">
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Обязательно указывать ячейку перед завершением сушки
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-gray-600 dark:text-gray-300">
                    Сотрудник цеха не сможет завершить сушку предмета потока «Чистка», пока не укажет ячейку временного хранения.
                  </span>
                </span>
                <span className="relative mt-0.5 inline-flex shrink-0">
                  <input
                    type="checkbox"
                    checked={systemSettings.cleaningStorageCellsRequired}
                    onChange={(event) => {
                      haptic('selection');
                      handleCheckboxChange('cleaningStorageCellsRequired', event.target.checked);
                    }}
                    className="peer sr-only"
                  />
                  <span className="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-orange-500 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-400 dark:bg-gray-700" />
                  <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
                </span>
              </label>
            </div>

            {/* Карточка текущего порядка ячеек */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-4 space-y-3">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                Текущий порядок ({storageCells.filter((c) => c.is_active).length} активных
                {storageCells.filter((c) => !c.is_active).length > 0 ? `, ${storageCells.filter((c) => !c.is_active).length} в архиве` : ''}):
              </div>

              {!storageCellsLoading && storageCells.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 py-6 text-center text-xs text-gray-500">
                  Ячеек пока нет. Нажмите «Настроить ячейки и порядок», чтобы добавить первую ячейку.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {storageCells.map((cell, idx) => (
                    <div
                      key={cell.id}
                      className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold ${
                        cell.is_active
                          ? 'border-gray-200 bg-white text-gray-800 shadow-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
                          : 'border-gray-200 bg-gray-100/60 text-gray-400 opacity-60 dark:border-gray-800 dark:bg-gray-900/40'
                      }`}
                    >
                      <span className="font-mono text-[10px] text-gray-400">#{idx + 1}</span>
                      <Boxes className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                      <span>{cell.name}</span>
                      {cell.active_placements > 0 && (
                        <span className="rounded-full bg-blue-100 px-1.5 py-0.2 text-[10px] font-bold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                          {cell.active_placements}
                        </span>
                      )}
                      {!cell.is_active && <span className="text-[10px] text-gray-400">(архив)</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Нижняя кнопка сохранения настроек ячеек */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-end">
              <button
                type="button"
                onClick={handleSaveStorage}
                disabled={savingTab === 'storage'}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm font-semibold shadow-sm w-full sm:w-auto"
              >
                {savingTab === 'storage' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Сохранение...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Сохранить настройки склада
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Модальное окно управления ячейками и порядком */}
          <ModalShell
            open={isStorageModalOpen}
            onClose={() => {
              haptic('close');
              setIsStorageModalOpen(false);
              setEditingStorageCellId(null);
            }}
            variant="sheet"
            expandable={false}
            maxHeight="80dvh"
            panelClassName="max-w-lg flex flex-col h-[80dvh] max-h-[80dvh]"
          >
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 p-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-950/40 dark:text-orange-400">
                  <Boxes className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Ячейки хранения</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    Порядок и управление ячейками склада
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setIsStorageModalOpen(false);
                  setEditingStorageCellId(null);
                }}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Добавление ячейки */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <FloatingField label="Название ячейки">
                    <input
                      type="text"
                      value={newStorageCellName}
                      onChange={(event) => setNewStorageCellName(event.target.value.slice(0, 80))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') createStorageCell();
                      }}
                      className="w-full px-4 py-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 outline-none"
                    />
                  </FloatingField>
                </div>
                <button
                  type="button"
                  onClick={createStorageCell}
                  disabled={!newStorageCellName.trim() || storageCellSaving}
                  className="inline-flex h-[52px] items-center justify-center gap-1.5 rounded-xl bg-orange-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  <Plus className="h-4 w-4" />
                  <span>Добавить</span>
                </button>
              </div>
              <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                Перетаскивайте ячейки за значок или нажимайте стрелки ↑ ↓ для изменения порядка их отображения в цехе.
              </p>
            </div>

            {/* Список ячеек */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
              {storageCells.length === 0 ? (
                <div className="py-8 text-center text-xs text-gray-400">
                  Ячеек пока нет. Введите название выше и нажмите «Добавить».
                </div>
              ) : (
                storageCells.map((cell, index) => {
                  const isEditing = editingStorageCellId === cell.id;
                  const isFirst = index === 0;
                  const isLast = index === storageCells.length - 1;
                  return (
                    <div
                      key={cell.id}
                      data-cell-id={cell.id}
                      draggable={!isEditing}
                      onDragStart={(event) => handleCellDragStart(event, cell.id)}
                      onDragOver={handleCellDragOver}
                      onDrop={(event) => handleCellDrop(event, cell.id)}
                      className={`flex items-center gap-2 rounded-xl border p-2.5 transition-all ${
                        touchDraggingId === cell.id
                          ? 'border-orange-400 bg-orange-50 shadow-lg ring-2 ring-orange-300 dark:bg-orange-950/40 z-10 scale-[1.02]'
                          : cell.is_active
                            ? 'border-gray-200 bg-white shadow-xs dark:border-gray-700/80 dark:bg-gray-800'
                            : 'border-gray-200 bg-gray-50/70 opacity-60 dark:border-gray-800 dark:bg-gray-900/50'
                      }`}
                    >
                      {/* Drag handle & Order number */}
                      <div
                        className="flex items-center gap-1 text-gray-400 cursor-grab active:cursor-grabbing select-none shrink-0 p-1 -m-1 touch-none"
                        title="Перетащите для изменения порядка"
                        style={{ touchAction: 'none' }}
                        onTouchStart={(event) => handleTouchStart(event, cell.id)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                      >
                        <GripVertical className="h-4 w-4" />
                        <span className="font-mono text-xs font-bold text-gray-500 w-5 text-center">
                          #{index + 1}
                        </span>
                      </div>

                      {/* Cell content or Edit input */}
                      <div className="min-w-0 flex-1">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingStorageCellName}
                            onChange={(event) => setEditingStorageCellName(event.target.value.slice(0, 80))}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') saveStorageCellName(cell.id);
                              if (event.key === 'Escape') setEditingStorageCellId(null);
                            }}
                            autoFocus
                            className="h-8 w-full rounded-lg border border-orange-400 bg-white dark:bg-gray-700 px-2.5 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-orange-200"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {cell.name}
                            </span>
                            {cell.active_placements > 0 && (
                              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 shrink-0">
                                {cell.active_placements} шт
                              </span>
                            )}
                            {!cell.is_active && (
                              <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300 shrink-0">
                                Архив
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {isEditing ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => saveStorageCellName(cell.id)}
                            disabled={!editingStorageCellName.trim() || storageCellSaving}
                            className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300"
                            title="Сохранить"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingStorageCellId(null)}
                            className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                            title="Отмена"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 shrink-0">
                          {/* Move Up / Down */}
                          <button
                            type="button"
                            onClick={() => moveStorageCell(cell.id, -1)}
                            disabled={isFirst || storageCellSaving}
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-20 disabled:hover:bg-transparent dark:hover:bg-gray-700 dark:hover:text-gray-200"
                            title="Переместить выше"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveStorageCell(cell.id, 1)}
                            disabled={isLast || storageCellSaving}
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-20 disabled:hover:bg-transparent dark:hover:bg-gray-700 dark:hover:text-gray-200"
                            title="Переместить ниже"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          {/* Edit */}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingStorageCellId(cell.id);
                              setEditingStorageCellName(cell.name);
                            }}
                            disabled={storageCellSaving}
                            className="p-1.5 rounded-lg text-gray-400 hover:bg-orange-50 hover:text-orange-600 dark:hover:bg-orange-950/30 dark:hover:text-orange-300"
                            title="Переименовать"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {/* Archive / Restore */}
                          <button
                            type="button"
                            onClick={() => {
                              if (cell.is_active && cell.active_placements > 0) {
                                showToast(
                                  `Нельзя убрать в архив: в ячейке находятся предметы активных заказов (${cell.active_placements} шт.)`,
                                  'warning'
                                );
                                return;
                              }
                              setStorageCellActive(cell, !cell.is_active);
                            }}
                            disabled={storageCellSaving}
                            className={`p-1.5 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${
                              cell.is_active
                                ? (cell.active_placements > 0
                                    ? 'text-amber-500 hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-950/30'
                                    : 'text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-300')
                                : 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30'
                            }`}
                            title={cell.is_active
                              ? (cell.active_placements > 0 ? `В ячейке ${cell.active_placements} предметов` : 'Убрать в архив')
                              : 'Восстановить'}
                          >
                            {cell.is_active ? <Archive className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setIsStorageModalOpen(false);
                  setEditingStorageCellId(null);
                }}
                className="px-5 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 font-semibold text-sm transition-colors"
              >
                Готово
              </button>
            </div>
          </ModalShell>
        </div>
      )}

      {/* ========================================================================= */}
      {/* МОДАЛЬНОЕ ОКНО: ВОССТАНОВЛЕНИЕ БАЗЫ ДАННЫХ                                */}
      {/* ========================================================================= */}
      {restoreTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900 border border-gray-200 dark:border-gray-800 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400 shrink-0">
                <RotateCcw className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Восстановление базы данных
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {restoreTarget.isUpload ? 'Загрузка файла резервной копии' : 'Локальный архив на сервере'}
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-200 leading-relaxed">
              <strong>⚠️ Внимание:</strong> Текущая рабочая база данных будет полностью заменена состоянием из этой копии.
              <div className="mt-1.5 flex items-center gap-1.5 font-semibold text-emerald-800 dark:text-emerald-300">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                Перед заменой система автоматически создаст страховочный снимок текущей базы данных.
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3.5 dark:border-gray-800 dark:bg-gray-800/40 space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Файл копии:</span>
                <span className="font-mono font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[260px]">
                  {restoreTarget.filename}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Размер:</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">{restoreTarget.size_mb} МБ</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Шифрование:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1">
                  {restoreTarget.is_encrypted ? (
                    <span className="text-purple-600 dark:text-purple-400 flex items-center gap-1">
                      <Lock className="h-3.5 w-3.5" />
                      Зашифрован (AES-256 ZIP)
                    </span>
                  ) : (
                    <span>Обычный архив (Gzip)</span>
                  )}
                </span>
              </div>
            </div>

            {restoreTarget.is_encrypted && (
              <div>
                <FloatingField label="Пароль для расшифровки архива *">
                  <div className="relative">
                    <input
                      type={showRestorePassword ? 'text' : 'password'}
                      value={restorePassword}
                      onChange={(e) => setRestorePassword(e.target.value)}
                      placeholder="Введите пароль архива"
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pr-10 text-sm font-mono dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRestorePassword(!showRestorePassword)}
                      className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      {showRestorePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FloatingField>
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Архив защищён паролем. Введите пароль для распаковки.
                </p>
              </div>
            )}

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  if (!restoring) {
                    setRestoreTarget(null);
                    setRestorePassword('');
                  }
                }}
                disabled={restoring}
                className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={executeRestore}
                disabled={restoring || (restoreTarget.is_encrypted && !restorePassword.trim())}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                {restoring ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Восстановление и проверка базы...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4" />
                    Подтвердить и восстановить
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      <ConfirmModal
        isOpen={!!backupToDelete}
        title="Удаление резервной копии"
        message={`Вы уверены, что хотите безвозвратно удалить файл «${backupToDelete}» с сервера?`}
        confirmText={deletingBackup ? 'Удаление...' : 'Удалить'}
        cancelText="Отмена"
        type="danger"
        onConfirm={confirmDeleteBackup}
        onCancel={() => setBackupToDelete(null)}
      />
    </div>
  );
};

export default SystemManagement;
